import React, { useState, useEffect, createContext, useContext } from "react";
import { useNavigate, useLocation, Routes, Route, Outlet } from "react-router-dom";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { 
  Building2, Users, Brain, FolderCog, CreditCard, Shield,
  ChevronRight, ChevronDown, ChevronUp, Save, Upload, Trash2, Plus,
  Palette, FileText, UserPlus, Lock, ShoppingCart, BookOpen,
  Sparkles, Milestone, GitBranch, DollarSign, RefreshCw,
  Link2, FileCheck, AlertTriangle, Download, Settings,
  Check, X, Edit, Eye, EyeOff, LogOut, Loader2
} from "lucide-react";
import { AcquisitionCommandPage } from "./AcquisitionCommandPage";
import { usePermissions } from "../contexts/PermissionsContext";


// Settings Context for sharing data across sub-pages
const SettingsContext = createContext(null);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error("useSettings must be used within SettingsProvider");
  }
  return context;
};

// Main Settings Layout Component
const SettingsPage = () => {
  const { can } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const [settings, setSettings] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({
    company: true,
    team: false,
    estimating: false,
    projects: false,
    finance: false,
    system: false,
    data: false
  });

  // Navigation structure
  const navSections = [
    {
      id: "company",
      label: "Company",
      icon: Building2,
      items: [
        { id: "profile", label: "Company Profile", path: "/settings/company/profile" },
        { id: "brand", label: "Brand Defaults", path: "/settings/company/brand" },
      ]
    },
    {
      id: "team",
      label: "Team & Access",
      icon: Users,
      items: [
        { id: "members", label: "Team Members", path: "/settings/team/members" },
        { id: "roles", label: "Roles & Permissions", path: "/settings/team/roles", adminOnly: true },
      ]
    },
    {
      id: "estimating",
      label: "Estimating & AI",
      icon: Brain,
      items: [
        { id: "suppliers", label: "Supplier Pricing", path: "/settings/estimating/suppliers" },
        { id: "catalogs", label: "Catalogs", path: "/settings/estimating/catalogs" },
        { id: "rules", label: "Estimating Rules (AI)", path: "/settings/estimating/rules" },
        { id: "risk", label: "Profit Guardrails", path: "/settings/estimating/risk" },
      ]
    },
    {
      id: "projects",
      label: "Projects & Operations",
      icon: FolderCog,
      items: [
        { id: "sow", label: "SOW Defaults", path: "/settings/projects/sow" },
        { id: "trades", label: "Trade / Phase Mapping", path: "/settings/projects/trades", adminOnly: true },
        { id: "changes", label: "Change Order Rules", path: "/settings/projects/changes" },
      ]
    },
    {
      id: "finance",
      label: "Finance & Payments",
      icon: CreditCard,
      items: [
        { id: "payments", label: "Payments Setup", path: "/settings/finance/payments" },
        { id: "client-payments", label: "Client Payments", path: "/settings/finance/client-payments" },
        { id: "deposits", label: "Deposits", path: "/settings/finance/deposits" },
        { id: "accounting", label: "Accounting Sync", path: "/settings/finance/accounting" },
        { id: "acquisition", label: "Acquisition Command", path: "/settings/finance/acquisition", adminOnly: true },
      ]
    },
    {
      id: "system",
      label: "System & Legal",
      icon: Shield,
      items: [
        { id: "terms", label: "Terms & Conditions", path: "/settings/system/terms" },
        { id: "legal", label: "Legal & Privacy", path: "/settings/system/legal" },
        { id: "subscription", label: "Subscription & Billing", path: "/settings/system/subscription" },
        { id: "danger", label: "Danger Zone", path: "/settings/system/danger", adminOnly: true },
      ]
    },
    {
      id: "data",
      label: "Data & Migration",
      icon: Upload,
      items: [
        { id: "import", label: "Import Data", path: "/settings/data/import" },
        { id: "export", label: "Export Data", path: "/settings/data/export" },
      ]
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, userRes] = await Promise.all([
          API.get("/settings"),
          API.get("/auth/me")
        ]);
        setSettings(settingsRes.data);
        setUser(userRes.data);
      } catch (error) {
        console.error("Failed to load settings", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Redirect to default page if on root /settings
  useEffect(() => {
    if (location.pathname === "/settings" || location.pathname === "/settings/") {
      navigate("/settings/company/profile", { replace: true });
    }
  }, [location.pathname, navigate]);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const isActive = (path) => location.pathname === path;
  const isAdmin = user?.role === "admin" || user?.role === "owner";

  const handleLogout = async () => {
    try {
      await API.post("/auth/logout");
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
      navigate("/login");
    }
  };

  const updateSettings = async (newSettings) => {
    try {
      await API.put("/settings", newSettings);
      setSettings(prev => ({ ...prev, ...newSettings }));
      toast.success("Settings saved");
      return true;
    } catch (error) {
      toast.error("Failed to save settings");
      return false;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  const isAcquisitionRoute = location.pathname === "/settings/finance/acquisition";

  return (
    <SettingsContext.Provider value={{ settings, setSettings, updateSettings, user, isAdmin }}>
      <div
        className={isAcquisitionRoute ? "w-full h-full" : "flex h-full"}
        style={{ overflow: 'hidden' }}
      >
        {/* Left Navigation Sidebar - SCROLLABLE */}
        {!isAcquisitionRoute && (
          <div 
            className="w-64 flex-shrink-0 bg-slate-900 border-r border-slate-800 flex flex-col"
            style={{ height: '100%' }}
          >
            <div className="p-4 pb-2 flex-shrink-0">
              <h1 className="text-xl font-chivo font-bold text-white">Settings</h1>
            </div>
            
            {/* Navigation - SCROLLABLE */}
            <nav className="px-2 pb-16 space-y-0.5 flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
              {navSections.map((section) => (
                <div key={section.id}>
                  <button
                    onClick={() => toggleSection(section.id)}
                    className="w-full flex items-center justify-between px-3 py-1 text-sm font-medium text-slate-300 hover:bg-slate-800 rounded-lg transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <section.icon size={14} className="text-slate-400" />
                      <span>{section.label}</span>
                    </div>
                    {expandedSections[section.id] ? (
                      <ChevronDown size={12} className="text-slate-400" />
                    ) : (
                      <ChevronRight size={12} className="text-slate-400" />
                    )}
                  </button>
                  {expandedSections[section.id] && (
                    <div className="ml-5 mt-0.5 space-y-0">
                      {section.items.map((item) => {
                        if (item.adminOnly && !isAdmin) return null;
                        return (
                          <button
                            key={item.id}
                            onClick={() => navigate(item.path)}
                            className={`w-full text-left px-3 py-1 text-xs rounded-lg transition-colors ${
                              isActive(item.path)
                                ? "bg-emerald-500/20 text-emerald-400 font-medium"
                                : "text-slate-400 hover:bg-slate-800"
                            }`}
                          >
                            {item.label}
                            {item.adminOnly && (
                              <Lock size={8} className="inline ml-1 text-slate-400" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* Sign Out - at bottom */}
            <div className="flex-shrink-0 p-2 border-t border-slate-700 bg-slate-900">
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
              >
                <LogOut size={16} />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        )}

        {/* Main Content Area - THIS IS THE ONLY SCROLLING AREA */}
        <div className={isAcquisitionRoute ? "w-full h-full" : "flex-1 h-full bg-slate-950"}>
          <div 
            className="h-full overflow-y-auto"
            style={{ scrollbarWidth: 'thin', scrollbarColor: '#475569 #1e293b' }}
          >
            <div className={isAcquisitionRoute ? "pb-12 w-full" : "p-6 pb-16 w-full"}>
              <Routes>
              {/* Company */}
              <Route path="company/profile" element={<CompanyProfile />} />
              <Route path="company/brand" element={<BrandDefaults />} />

              {/* Finance & Acquisition */}
              <Route path="finance/acquisition" element={<AcquisitionCommandPage />} />
            
            {/* Team & Access */}
            <Route path="team/members" element={<TeamMembers />} />
            <Route path="team/roles" element={<RolesPermissions />} />
            
            {/* Estimating & AI */}
            <Route path="estimating/suppliers" element={<SupplierPricing />} />
            <Route path="estimating/catalogs" element={<Catalogs />} />
            <Route path="estimating/rules" element={<EstimatingRules />} />
            <Route path="estimating/risk" element={<ProfitGuardrails />} />
            
            {/* Projects & Operations */}
            <Route path="projects/sow" element={<SOWDefaults />} />
            <Route path="projects/trades" element={<TradePhaseMapping />} />
            <Route path="projects/changes" element={<ChangeOrderRules />} />
            
            {/* Finance & Payments */}
            <Route path="finance/payments" element={<PaymentsSetup />} />
            <Route path="finance/client-payments" element={<ClientPaymentsSettings />} />
            <Route path="finance/deposits" element={<Deposits />} />
            <Route path="finance/accounting" element={<AccountingSync />} />
            
            {/* System & Legal */}
            <Route path="system/terms" element={<TermsConditions />} />
            <Route path="system/legal" element={<LegalPrivacy />} />
            <Route path="system/subscription" element={<SubscriptionBilling />} />
            <Route path="system/danger" element={<DangerZone />} />
            
            {/* Data & Migration */}
            <Route path="data/import" element={<DataImportPage />} />
            <Route path="data/export" element={<DataExportPage />} />
            
            {/* Default */}
            <Route path="*" element={<CompanyProfile />} />
          </Routes>
            </div>
          </div>
        </div>
      </div>
    </SettingsContext.Provider>
  );
};

// ================== COMPANY SECTION ==================

const CompanyProfile = () => {
  const { settings, updateSettings } = useSettings();
  const [form, setForm] = useState({
    company_name: settings?.company_name || "",
    license_number: settings?.license_number || "",
    company_email: settings?.company_email || "",
    company_phone: settings?.company_phone || "",
    company_address: settings?.company_address || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  const handleSave = async () => {
    setSaving(true);
    await updateSettings(form);
    setSaving(false);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please upload an image file (PNG, JPG)");
      return;
    }
    
    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("File size must be less than 2MB");
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('file_type', 'logo');
      
      const res = await API.post('/upload-file', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Update settings with new logo URL
      const logoUrl = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      await updateSettings({ company_logo: logoUrl });
      toast.success("Logo uploaded successfully!");
    } catch (error) {
      toast.error("Failed to upload logo");
      console.error(error);
    } finally {
      setUploading(false);
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveLogo = async () => {
    try {
      await updateSettings({ company_logo: null });
      toast.success("Logo removed");
    } catch (error) {
      toast.error("Failed to remove logo");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-chivo font-bold text-white">Company Profile</h2>
        <p className="text-slate-400 text-sm mt-1">Basic information about your company</p>
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="pt-6 space-y-4">
          {/* Logo Upload */}
          <div>
            <label className="text-sm font-medium text-slate-300 mb-2 block">Company Logo</label>
            <div className="flex items-center gap-4">
              <div className="h-20 w-20 rounded-lg bg-slate-800 flex items-center justify-center border-2 border-dashed border-slate-300 overflow-hidden">
                {settings?.company_logo ? (
                  <img src={settings.company_logo} alt="Logo" className="h-full w-full object-cover rounded-lg" />
                ) : (
                  <Building2 className="h-8 w-8 text-slate-400" />
                )}
              </div>
              <div className="space-y-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleLogoUpload}
                  accept="image/png,image/jpeg,image/jpg"
                  className="hidden"
                />
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="border-slate-700 text-slate-300 hover:bg-slate-800"
                  >
                    {uploading ? (
                      <><RefreshCw size={14} className="mr-2 animate-spin" /> Uploading...</>
                    ) : (
                      <><Upload size={14} className="mr-2" /> Upload Logo</>
                    )}
                  </Button>
                  {settings?.company_logo && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={handleRemoveLogo}
                      className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 size={14} className="mr-2" /> Remove
                    </Button>
                  )}
                </div>
                <p className="text-xs text-slate-400">PNG, JPG up to 2MB. Recommended 200x200px</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-300 mb-1 block">Company Name</label>
              <Input
                value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                placeholder="Your Company Name"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300 mb-1 block">License Number</label>
              <Input
                value={form.license_number}
                onChange={(e) => setForm({ ...form, license_number: e.target.value })}
                placeholder="e.g., CSLB #123456"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-300 mb-1 block">Email</label>
              <Input
                type="email"
                value={form.company_email}
                onChange={(e) => setForm({ ...form, company_email: e.target.value })}
                placeholder="info@company.com"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300 mb-1 block">Phone</label>
              <Input
                value={form.company_phone}
                onChange={(e) => setForm({ ...form, company_phone: e.target.value })}
                placeholder="(555) 123-4567"
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-slate-300 mb-1 block">Address</label>
            <Textarea
              value={form.company_address}
              onChange={(e) => setForm({ ...form, company_address: e.target.value })}
              placeholder="123 Main St, City, State ZIP"
              rows={2}
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">
          <Save size={16} className="mr-2" />
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </div>
  );
};

const BrandDefaults = () => {
  const { settings, updateSettings } = useSettings();
  const [form, setForm] = useState({
    primary_color: settings?.primary_color || "#3b82f6",
    accent_color: settings?.accent_color || "#10b981",
    default_proposal_theme: settings?.default_proposal_theme || "professional",
    document_footer_text: settings?.document_footer_text || "",
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await updateSettings(form);
    setSaving(false);
  };

  const themes = [
    { id: "professional", label: "Professional", description: "Clean and corporate" },
    { id: "modern", label: "Modern", description: "Contemporary and minimal" },
    { id: "classic", label: "Classic", description: "Traditional and formal" },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-chivo font-bold text-white">Brand Defaults</h2>
        <p className="text-slate-400 text-sm mt-1">Customize your documents and proposals</p>
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Palette size={18} />
            Colors
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-300 mb-1 block">Primary Color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={form.primary_color}
                  onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                  className="h-10 w-14 rounded border cursor-pointer"
                />
                <Input
                  value={form.primary_color}
                  onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                  className="flex-1"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300 mb-1 block">Accent Color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={form.accent_color}
                  onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                  className="h-10 w-14 rounded border cursor-pointer"
                />
                <Input
                  value={form.accent_color}
                  onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                  className="flex-1"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText size={18} />
            Proposal Theme
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-3">
            {themes.map((theme) => (
              <button
                key={theme.id}
                onClick={() => setForm({ ...form, default_proposal_theme: theme.id })}
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  form.default_proposal_theme === theme.id
                    ? "border-emerald-500 bg-emerald-500/10"
                    : "border-slate-700 hover:border-slate-300"
                }`}
              >
                <p className="font-medium text-white">{theme.label}</p>
                <p className="text-xs text-slate-400 mt-1">{theme.description}</p>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-lg">Document Footer</CardTitle>
          <CardDescription>This text appears at the bottom of all generated documents</CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={form.document_footer_text}
            onChange={(e) => setForm({ ...form, document_footer_text: e.target.value })}
            placeholder="e.g., Licensed & Insured | CA License #123456"
            rows={2}
          />
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">
          <Save size={16} className="mr-2" />
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </div>
  );
};

// ================== TEAM & ACCESS SECTION ==================

const TeamMembers = () => {
  const [team, setTeam] = useState([]);
  const [inviteData, setInviteData] = useState({ active: [], pending: [], deactivated: [], limit: 8, current_count: 0, can_invite: true });
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [newMember, setNewMember] = useState({ name: "", email: "", phone: "", job_title: "", role: "field", message: "" });
  const [inviteSending, setInviteSending] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchTeamData();
  }, []);

  const fetchTeamData = async () => {
    try {
      // Fetch both old team data and new invite data
      const [teamResponse, inviteResponse] = await Promise.all([
        API.get("/team").catch(() => ({ data: [] })),
        API.get("/users/team").catch(() => ({ data: { active: [], pending: [], deactivated: [], limit: 8, current_count: 0, can_invite: true } }))
      ]);
      setTeam(teamResponse.data || []);
      setInviteData(inviteResponse.data || { active: [], pending: [], deactivated: [], limit: 8, current_count: 0, can_invite: true });
    } catch (error) {
      console.error("Failed to load team", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async () => {
    if (!newMember.email || !newMember.name) {
      toast.error("Name and email are required");
      return;
    }

    setInviteSending(true);
    try {
      // Parse name into first/last
      const nameParts = newMember.name.trim().split(" ");
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(" ") || "";

      // Send invite via the new invite API
      const response = await API.post("/users/team/invite", {
        email: newMember.email,
        first_name: firstName,
        last_name: lastName,
        phone: newMember.phone,
        job_title: newMember.job_title,
        role: newMember.role,
        message: newMember.message
      });

      setAddDialogOpen(false);
      setNewMember({ name: "", email: "", phone: "", job_title: "", role: "field", message: "" });
      toast.success(`Invitation sent to ${newMember.email}`);
      fetchTeamData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invite");
    } finally {
      setInviteSending(false);
    }
  };

  const handleResendInvite = async (userId) => {
    setActionLoading(userId);
    try {
      await API.post(`/users/team/${userId}/resend-invite`);
      toast.success("Invitation resent");
      fetchTeamData();
    } catch (error) {
      toast.error("Failed to resend invite");
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelInvite = async (userId) => {
    if (!window.confirm("Cancel this invitation?")) return;
    setActionLoading(userId);
    try {
      await API.delete(`/users/team/${userId}/cancel-invite`);
      toast.success("Invitation cancelled");
      fetchTeamData();
    } catch (error) {
      toast.error("Failed to cancel invite");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeactivate = async (userId) => {
    if (!window.confirm("Deactivate this team member?")) return;
    setActionLoading(userId);
    try {
      await API.post(`/users/team/${userId}/deactivate`);
      toast.success("Team member deactivated");
      fetchTeamData();
    } catch (error) {
      toast.error("Failed to deactivate");
    } finally {
      setActionLoading(null);
    }
  };

  const handleReactivate = async (userId) => {
    setActionLoading(userId);
    try {
      await API.post(`/users/team/${userId}/reactivate`);
      toast.success("Team member reactivated");
      fetchTeamData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reactivate");
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm("Permanently remove this team member? This cannot be undone.")) return;
    setActionLoading(userId);
    try {
      await API.delete(`/users/team/${userId}`);
      toast.success("Team member removed");
      fetchTeamData();
    } catch (error) {
      toast.error("Failed to remove");
    } finally {
      setActionLoading(null);
    }
  };

  const handleEditMember = (member) => {
    setEditingMember({ ...member });
    setEditDialogOpen(true);
  };

  const handleUpdateMember = async () => {
    try {
      await API.put(`/users/team/${editingMember.user_id}`, editingMember);
      setEditDialogOpen(false);
      setEditingMember(null);
      toast.success("Team member updated");
      fetchTeamData();
    } catch (error) {
      toast.error("Failed to update team member");
    }
  };

  // All 56 roles - same as Roles & Permissions
  const allRoles = [
    // Executive/Leadership
    { id: "owner", label: "Owner", description: "Company owner - full access", color: "bg-amber-500", category: "executive" },
    { id: "ceo", label: "CEO", description: "Chief Executive Officer", color: "bg-amber-600", category: "executive" },
    { id: "coo", label: "COO", description: "Chief Operating Officer", color: "bg-amber-700", category: "executive" },
    { id: "cfo", label: "CFO", description: "Chief Financial Officer", color: "bg-amber-800", category: "executive" },
    { id: "president", label: "President", description: "Company President", color: "bg-yellow-600", category: "executive" },
    { id: "vp", label: "Vice President", description: "VP - senior management", color: "bg-yellow-700", category: "executive" },
    // Operations Management
    { id: "general_manager", label: "General Manager", description: "Overall operations", color: "bg-purple-500", category: "management" },
    { id: "operations_manager", label: "Operations Manager", description: "Day-to-day operations", color: "bg-purple-600", category: "management" },
    { id: "construction_manager", label: "Construction Manager", description: "Construction oversight", color: "bg-purple-700", category: "management" },
    { id: "pm", label: "Project Manager", description: "Project planning", color: "bg-blue-500", category: "management" },
    { id: "assistant_pm", label: "Assistant PM", description: "PM support", color: "bg-blue-600", category: "management" },
    // Site Leadership
    { id: "superintendent", label: "Superintendent", description: "Multi-site supervision", color: "bg-indigo-500", category: "site" },
    { id: "site_supervisor", label: "Site Supervisor", description: "On-site supervision", color: "bg-indigo-600", category: "site" },
    { id: "foreman", label: "Foreman", description: "Crew leader", color: "bg-indigo-700", category: "site" },
    { id: "lead_carpenter", label: "Lead Carpenter", description: "Trade lead", color: "bg-indigo-800", category: "site" },
    // Administrative
    { id: "admin", label: "Admin", description: "System administrator", color: "bg-rose-500", category: "admin" },
    { id: "office_manager", label: "Office Manager", description: "Office operations", color: "bg-rose-600", category: "admin" },
    { id: "office", label: "Office Staff", description: "General office support", color: "bg-emerald-500", category: "admin" },
    { id: "receptionist", label: "Receptionist", description: "Front desk", color: "bg-emerald-600", category: "admin" },
    { id: "admin_assistant", label: "Admin Assistant", description: "Administrative support", color: "bg-emerald-700", category: "admin" },
    // Finance & Accounting
    { id: "controller", label: "Controller", description: "Financial controller", color: "bg-green-500", category: "finance" },
    { id: "bookkeeper", label: "Bookkeeper", description: "Financial records", color: "bg-green-600", category: "finance" },
    { id: "accountant", label: "Accountant", description: "Financial reporting", color: "bg-green-700", category: "finance" },
    { id: "ap_clerk", label: "AP Clerk", description: "Accounts payable", color: "bg-green-800", category: "finance" },
    { id: "ar_clerk", label: "AR Clerk", description: "Accounts receivable", color: "bg-lime-600", category: "finance" },
    { id: "payroll", label: "Payroll Specialist", description: "Payroll processing", color: "bg-lime-700", category: "finance" },
    // Sales & Business Development
    { id: "sales_manager", label: "Sales Manager", description: "Sales team management", color: "bg-cyan-500", category: "sales" },
    { id: "sales_rep", label: "Sales Rep", description: "Client acquisition", color: "bg-cyan-600", category: "sales" },
    { id: "business_dev", label: "Business Development", description: "New business", color: "bg-cyan-700", category: "sales" },
    { id: "estimator", label: "Estimator", description: "Project estimation", color: "bg-teal-500", category: "sales" },
    { id: "senior_estimator", label: "Senior Estimator", description: "Complex estimates", color: "bg-teal-600", category: "sales" },
    { id: "bid_manager", label: "Bid Manager", description: "Bid coordination", color: "bg-teal-700", category: "sales" },
    // Field Operations
    { id: "field", label: "Field Crew", description: "General field workers", color: "bg-orange-500", category: "field" },
    { id: "journeyman", label: "Journeyman", description: "Skilled tradesperson", color: "bg-orange-600", category: "field" },
    { id: "apprentice", label: "Apprentice", description: "Trade apprentice", color: "bg-orange-700", category: "field" },
    { id: "laborer", label: "Laborer", description: "General labor", color: "bg-orange-800", category: "field" },
    { id: "helper", label: "Helper", description: "Trade helper", color: "bg-red-600", category: "field" },
    // Safety & Quality
    { id: "safety_manager", label: "Safety Manager", description: "Safety program", color: "bg-red-500", category: "safety" },
    { id: "safety_officer", label: "Safety Officer", description: "Safety compliance", color: "bg-red-600", category: "safety" },
    { id: "qc_manager", label: "QC Manager", description: "Quality control", color: "bg-red-700", category: "safety" },
    { id: "qc_inspector", label: "QC Inspector", description: "Quality inspections", color: "bg-red-800", category: "safety" },
    // Purchasing & Warehouse
    { id: "purchasing_manager", label: "Purchasing Manager", description: "Procurement", color: "bg-violet-500", category: "purchasing" },
    { id: "purchasing_agent", label: "Purchasing Agent", description: "Material purchasing", color: "bg-violet-600", category: "purchasing" },
    { id: "warehouse_manager", label: "Warehouse Manager", description: "Inventory", color: "bg-violet-700", category: "purchasing" },
    { id: "warehouse_staff", label: "Warehouse Staff", description: "Warehouse ops", color: "bg-violet-800", category: "purchasing" },
    { id: "equipment_manager", label: "Equipment Manager", description: "Equipment/fleet", color: "bg-fuchsia-600", category: "purchasing" },
    // HR & Support
    { id: "hr_manager", label: "HR Manager", description: "Human resources", color: "bg-pink-500", category: "hr" },
    { id: "hr_coordinator", label: "HR Coordinator", description: "HR support", color: "bg-pink-600", category: "hr" },
    { id: "recruiter", label: "Recruiter", description: "Talent acquisition", color: "bg-pink-700", category: "hr" },
    { id: "training_coordinator", label: "Training Coordinator", description: "Training programs", color: "bg-pink-800", category: "hr" },
    // IT & Marketing
    { id: "it_admin", label: "IT Admin", description: "Technology/systems", color: "bg-slate-500", category: "support" },
    { id: "marketing_manager", label: "Marketing Manager", description: "Marketing/branding", color: "bg-slate-600", category: "support" },
    // External/Limited
    { id: "client_viewer", label: "Client Viewer", description: "Client portal only", color: "bg-gray-500", category: "external" },
    { id: "subcontractor", label: "Subcontractor", description: "Sub portal access", color: "bg-gray-600", category: "external" },
    { id: "vendor", label: "Vendor", description: "Vendor/supplier access", color: "bg-gray-700", category: "external" },
    { id: "consultant", label: "Consultant", description: "External consultant", color: "bg-gray-800", category: "external" },
  ];

  const roleCategories = [
    { id: "executive", label: "Executive / Leadership" },
    { id: "management", label: "Operations Management" },
    { id: "site", label: "Site Leadership" },
    { id: "admin", label: "Administrative" },
    { id: "finance", label: "Finance & Accounting" },
    { id: "sales", label: "Sales & Estimating" },
    { id: "field", label: "Field Operations" },
    { id: "safety", label: "Safety & Quality" },
    { id: "purchasing", label: "Purchasing & Warehouse" },
    { id: "hr", label: "HR & Training" },
    { id: "support", label: "IT & Marketing" },
    { id: "external", label: "External / Limited" },
  ];

  const getRoleBadge = (roleId) => {
    const role = allRoles.find(r => r.id === roleId);
    if (!role) return <Badge className="bg-slate-800">{roleId?.toUpperCase() || 'UNKNOWN'}</Badge>;
    return <Badge className={`${role.color}/20 text-white`}>{role.label}</Badge>;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-emerald-500/20 text-emerald-400">Active</Badge>;
      case 'pending_invite':
        return <Badge className="bg-amber-500/20 text-amber-400">Pending Invite</Badge>;
      case 'deactivated':
        return <Badge className="bg-slate-500/20 text-slate-400">Deactivated</Badge>;
      default:
        return <Badge className="bg-slate-500/20 text-slate-400">{status}</Badge>;
    }
  };

  // Combine all members for display
  const allMembers = [...(inviteData.active || []), ...(inviteData.pending || [])];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-chivo font-bold text-white">Team Members</h2>
          <p className="text-slate-400 text-sm mt-1">Manage who has access to your account</p>
        </div>
        <Button 
          onClick={() => setAddDialogOpen(true)} 
          disabled={!inviteData.can_invite}
          className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold disabled:opacity-50"
        >
          <UserPlus size={16} className="mr-2" />
          Add Team Member
        </Button>
      </div>

      {/* Team Limit Info */}
      <div className="flex items-center gap-3 px-4 py-3 bg-slate-800/50 rounded-lg border border-slate-700">
        <Users size={18} className="text-emerald-400" />
        <span className="text-slate-300">
          Team Members: <strong className="text-white">{inviteData.current_count || 0}</strong> of {inviteData.limit || 8}
        </span>
        {!inviteData.can_invite && (
          <span className="text-amber-400 text-sm ml-auto">Team limit reached</span>
        )}
      </div>

      {/* Active Members */}
      {inviteData.active?.length > 0 && (
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-sm font-semibold uppercase tracking-wider">
              Active Members ({inviteData.active.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="divide-y divide-slate-800">
              {inviteData.active.map((member) => (
                <div key={member.user_id} className="py-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                      <span className="text-sm font-medium text-emerald-400">
                        {member.first_name?.[0] || member.name?.[0] || "?"}
                        {member.last_name?.[0] || ""}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-white">{member.first_name ? `${member.first_name} ${member.last_name}` : member.name}</p>
                      <p className="text-sm text-slate-400">{member.email}</p>
                      {member.job_title && <p className="text-xs text-slate-500">{member.job_title}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getRoleBadge(member.role)}
                    {member.role !== 'owner' && (
                      <>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-slate-400 hover:text-blue-400 hover:bg-slate-800"
                          onClick={() => handleEditMember(member)}
                        >
                          <Edit size={14} />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-slate-400 hover:text-red-400 hover:bg-slate-800"
                          onClick={() => handleDeactivate(member.user_id)}
                          disabled={actionLoading === member.user_id}
                        >
                          {actionLoading === member.user_id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pending Invites */}
      {inviteData.pending?.length > 0 && (
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-amber-400 text-sm font-semibold uppercase tracking-wider">
              Pending Invites ({inviteData.pending.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="divide-y divide-slate-800">
              {inviteData.pending.map((member) => (
                <div key={member.user_id} className="py-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                      <span className="text-sm font-medium text-amber-400">
                        {member.first_name?.[0] || "?"}
                        {member.last_name?.[0] || ""}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-white">{member.first_name} {member.last_name}</p>
                      <p className="text-sm text-slate-400">{member.email}</p>
                      {member.job_title && <p className="text-xs text-slate-500">{member.job_title}</p>}
                      <p className="text-xs text-amber-400 mt-1">Invite sent • Expires in 48 hours</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getRoleBadge(member.role)}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-slate-300 border-slate-700 hover:bg-slate-800"
                      onClick={() => handleResendInvite(member.user_id)}
                      disabled={actionLoading === member.user_id}
                    >
                      {actionLoading === member.user_id ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                      <span className="ml-1.5">Resend</span>
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-red-400 hover:text-red-300 hover:bg-slate-800"
                      onClick={() => handleCancelInvite(member.user_id)}
                      disabled={actionLoading === member.user_id}
                    >
                      <X size={14} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Deactivated Members */}
      {inviteData.deactivated?.length > 0 && (
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-slate-500 text-sm font-semibold uppercase tracking-wider">
              Deactivated ({inviteData.deactivated.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="divide-y divide-slate-800">
              {inviteData.deactivated.map((member) => (
                <div key={member.user_id} className="py-4 flex items-center justify-between opacity-60">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-slate-700 flex items-center justify-center">
                      <span className="text-sm font-medium text-slate-500">
                        {member.first_name?.[0] || member.name?.[0] || "?"}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-slate-400">{member.first_name ? `${member.first_name} ${member.last_name}` : member.name}</p>
                      <p className="text-sm text-slate-500">{member.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/10"
                      onClick={() => handleReactivate(member.user_id)}
                      disabled={actionLoading === member.user_id || !inviteData.can_invite}
                    >
                      {actionLoading === member.user_id ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                      <span className="ml-1.5">Reactivate</span>
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-red-400 hover:text-red-300 hover:bg-slate-800"
                      onClick={() => handleRemoveMember(member.user_id)}
                      disabled={actionLoading === member.user_id}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {allMembers.length === 0 && !loading && (
        <div className="text-center py-12 bg-slate-800/50 rounded-xl border border-slate-700">
          <Users size={48} className="mx-auto mb-4 text-slate-600" />
          <h3 className="text-white font-semibold mb-2">No Team Members Yet</h3>
          <p className="text-slate-400 mb-4">Invite your first team member to get started</p>
          <Button onClick={() => setAddDialogOpen(true)} className="bg-emerald-500 hover:bg-emerald-400 text-slate-900">
            <UserPlus size={16} className="mr-2" />
            Add Team Member
          </Button>
        </div>
      )}

      {/* Add Member Dialog with Invite */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-3xl p-0 overflow-hidden" style={{ maxHeight: '90vh', height: 'auto' }}>
          <div className="flex flex-col h-full" style={{ maxHeight: '90vh' }}>
            <DialogHeader className="flex-shrink-0 p-6 pb-4 border-b border-slate-800">
              <DialogTitle className="text-white">Add Team Member</DialogTitle>
            </DialogHeader>
            
            {/* Scrollable Content Area */}
            <div className="flex-1 overflow-y-auto px-6 py-4" style={{ maxHeight: 'calc(90vh - 180px)' }}>
              {/* Name & Email */}
              <div className="space-y-4 mb-6">
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-1 block">Name <span className="text-red-400">*</span></label>
                  <Input
                    value={newMember.name}
                    onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                    placeholder="John Smith"
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-1 block">Email <span className="text-red-400">*</span></label>
                  <Input
                    type="email"
                    value={newMember.email}
                    onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                    placeholder="john@company.com"
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-300 mb-1 block">Phone</label>
                    <Input
                      type="tel"
                      value={newMember.phone}
                      onChange={(e) => setNewMember({ ...newMember, phone: e.target.value })}
                      placeholder="(555) 123-4567"
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-300 mb-1 block">Job Title</label>
                    <Input
                      value={newMember.job_title}
                      onChange={(e) => setNewMember({ ...newMember, job_title: e.target.value })}
                      placeholder="Site Supervisor"
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                </div>
              </div>

              {/* Role Selection - Now properly scrollable within the content area */}
              <div className="mb-6">
                <label className="text-sm font-medium text-slate-300 mb-3 block">
                  Select Role <span className="text-slate-500">(required)</span>
                </label>
                <div className="space-y-4 border border-slate-700 rounded-lg p-4 bg-slate-800/30">
                  {roleCategories.map(cat => {
                    const categoryRoles = allRoles.filter(r => r.category === cat.id);
                    if (categoryRoles.length === 0) return null;
                    return (
                      <div key={cat.id}>
                        <p className="text-xs font-semibold text-emerald-400 uppercase tracking-wider mb-2">{cat.label}</p>
                        <div className="grid grid-cols-2 gap-2">
                          {categoryRoles.map((role) => (
                            <button
                              key={role.id}
                              type="button"
                              onClick={() => setNewMember({ ...newMember, role: role.id })}
                              className={`p-3 rounded-lg border text-left transition-all ${
                                newMember.role === role.id
                                  ? "border-emerald-500 bg-emerald-500/20 ring-1 ring-emerald-500"
                                  : "border-slate-600 hover:border-slate-400 bg-slate-800/50"
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <div className={`w-2.5 h-2.5 rounded-full ${role.color}`}></div>
                                <p className="font-medium text-white text-sm">{role.label}</p>
                              </div>
                              <p className="text-xs text-slate-400 mt-1 ml-4">{role.description}</p>
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Personal Message */}
              <div>
                <label className="text-sm font-medium text-slate-300 mb-1 block">Personal Message <span className="text-slate-500">(optional)</span></label>
                <textarea
                  value={newMember.message}
                  onChange={(e) => setNewMember({ ...newMember, message: e.target.value })}
                  placeholder="Hey, I'm setting you up on our project management system..."
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-md text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                  rows={2}
                />
              </div>
            </div>
            
            {/* Fixed Footer */}
            <div className="flex-shrink-0 flex justify-end gap-2 p-6 pt-4 border-t border-slate-800 bg-slate-900">
              <Button variant="outline" onClick={() => setAddDialogOpen(false)} className="border-slate-700 text-slate-300 hover:bg-slate-800">Cancel</Button>
              <Button 
                onClick={handleAddMember} 
                disabled={!newMember.email || !newMember.name || inviteSending} 
                className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold"
              >
                {inviteSending ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <UserPlus size={16} className="mr-2" />
                    Add Team Member (Send Invite)
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Member Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[85vh] flex flex-col p-0">
          <DialogHeader className="flex-shrink-0 p-6 pb-4 border-b border-slate-800">
            <DialogTitle className="text-white">Edit Team Member</DialogTitle>
          </DialogHeader>
          {editingMember && (
            <>
              {/* Fixed Name/Email section */}
              <div className="flex-shrink-0 px-6 pt-4 space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-1 block">Name</label>
                  <Input
                    value={editingMember.name || `${editingMember.first_name || ''} ${editingMember.last_name || ''}`}
                    onChange={(e) => setEditingMember({ ...editingMember, name: e.target.value })}
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-1 block">Email</label>
                  <Input
                    type="email"
                    value={editingMember.email || ""}
                    disabled
                    className="opacity-60"
                  />
                  <p className="text-xs text-slate-500 mt-1">Email cannot be changed</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-300 mb-1 block">Phone</label>
                    <Input
                      type="tel"
                      value={editingMember.phone || ""}
                      onChange={(e) => setEditingMember({ ...editingMember, phone: e.target.value })}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-300 mb-1 block">Job Title</label>
                    <Input
                      value={editingMember.job_title || ""}
                      onChange={(e) => setEditingMember({ ...editingMember, job_title: e.target.value })}
                      placeholder="Site Supervisor"
                    />
                  </div>
                </div>
                <label className="text-sm font-medium text-slate-300 block">Role <span className="text-slate-500">(scroll to see all)</span></label>
              </div>
              
              {/* Scrollable Role Selection */}
              <div className="flex-1 overflow-y-auto px-6 min-h-0" style={{ maxHeight: '40vh' }}>
                <div className="space-y-4 pb-4">
                  {roleCategories.map(cat => {
                    const categoryRoles = allRoles.filter(r => r.category === cat.id);
                    if (categoryRoles.length === 0) return null;
                    return (
                      <div key={cat.id}>
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 sticky top-0 bg-slate-900 py-1">{cat.label}</p>
                        <div className="grid grid-cols-2 gap-2">
                          {categoryRoles.map((role) => (
                            <button
                              key={role.id}
                              type="button"
                              onClick={() => setEditingMember({ ...editingMember, role: role.id })}
                              className={`p-2 rounded-lg border text-left transition-all ${
                                editingMember.role === role.id
                                  ? "border-emerald-500 bg-emerald-500/10"
                                  : "border-slate-700 hover:border-slate-500"
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <div className={`w-2 h-2 rounded-full ${role.color}`}></div>
                                <p className="font-medium text-white text-sm">{role.label}</p>
                              </div>
                              <p className="text-xs text-slate-400 ml-4">{role.description}</p>
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Fixed Footer */}
              <div className="flex-shrink-0 flex justify-end gap-2 p-6 pt-4 border-t border-slate-800">
                <Button variant="outline" onClick={() => { setEditDialogOpen(false); setEditingMember(null); }} className="border-slate-700 text-slate-300 hover:bg-slate-800">Cancel</Button>
                <Button onClick={handleUpdateMember} className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">Save Changes</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ================== ROLES & PERMISSIONS SECTION ==================

const RolesPermissions = () => {
  const { isAdmin } = useSettings();
  
  // Comprehensive permission categories covering the ENTIRE app
  const permissionCategories = {
    navigation: {
      label: "Navigation & Pages",
      description: "Control which pages and sections each role can access",
      permissions: {
        view_dashboard: { label: "Command Center (Dashboard)", description: "View main dashboard" },
        view_leads: { label: "Targets & Assets (Leads/Clients)", description: "Access leads and clients page" },
        view_calendar: { label: "Calendar", description: "Access calendar and appointments" },
        view_field_visit: { label: "Field Visit Calendar", description: "Access field visit scheduling" },
        view_estimating: { label: "Mission Scope (Estimating)", description: "Access estimating module" },
        view_blueprint_takeoff: { label: "Blueprint Takeoff", description: "Access blueprint takeoff tool" },
        view_estimates: { label: "Estimates List", description: "View all estimates" },
        view_proposals: { label: "Proposals", description: "Access proposals page" },
        view_projects: { label: "Active Operations (Projects)", description: "Access project management" },
        view_subcontractors: { label: "Subcontractors", description: "Access subcontractor management" },
        view_walkthroughs: { label: "Walkthroughs", description: "Access walkthrough scheduling" },
        view_purchase_orders: { label: "Purchase Orders", description: "Access PO management" },
        view_time_tracking: { label: "Duty Logs (Time Tracking)", description: "Access time tracking" },
        view_job_costing: { label: "Job Costing", description: "Access job costing reports" },
        view_payments: { label: "Payments", description: "Access payments page" },
        view_documents: { label: "Documents", description: "Access document management" },
        view_team: { label: "Team & Roles", description: "Access team management" },
        view_reports: { label: "Reports", description: "Access reporting dashboard" },
        view_settings: { label: "Settings", description: "Access settings page" },
      }
    },
    estimates: {
      label: "Estimates & Proposals",
      description: "Control estimate and proposal actions",
      permissions: {
        create_estimate: { label: "Create New Estimate", description: "Create new estimates" },
        edit_estimate: { label: "Edit Estimates", description: "Modify existing estimates" },
        delete_estimate: { label: "Delete Estimates", description: "Remove estimates" },
        duplicate_estimate: { label: "Duplicate Estimates", description: "Clone existing estimates" },
        mark_estimate_won: { label: "Mark Estimate as Won", description: "Convert estimates to projects" },
        mark_estimate_lost: { label: "Mark Estimate as Lost", description: "Mark estimates as lost" },
        view_estimate_costs: { label: "View Estimate Costs", description: "See cost breakdowns" },
        edit_estimate_pricing: { label: "Edit Pricing/Margins", description: "Modify pricing and margins" },
        use_ai_estimating: { label: "Use AI Estimating", description: "Access AI-powered estimating tools" },
        export_estimate: { label: "Export Estimates", description: "Export estimates to PDF/Excel" },
      }
    },
    proposals: {
      label: "Proposals & Documents",
      description: "Control proposal and document actions",
      permissions: {
        create_proposal: { label: "Create Proposals", description: "Generate proposals from estimates" },
        edit_proposal: { label: "Edit Proposals", description: "Modify proposal content" },
        send_proposal: { label: "Send Proposals to Clients", description: "Send proposals via email" },
        view_proposal_analytics: { label: "View Proposal Analytics", description: "See open/view rates" },
        create_operator_order: { label: "Create Operator Orders", description: "Generate internal work orders" },
        create_client_sow: { label: "Create Client SOW", description: "Generate client scopes of work" },
        create_sub_sow: { label: "Create Subcontractor SOW", description: "Generate sub SOWs" },
        create_milestone_sow: { label: "Create Milestone Objectives", description: "Generate milestone documents" },
        create_supply_manifest: { label: "Create Supply Manifest", description: "Generate material lists" },
      }
    },
    projects: {
      label: "Project Management",
      description: "Control project operations and tracking",
      permissions: {
        view_project_overview: { label: "View Project Overview", description: "Access project dashboard" },
        view_project_timeline: { label: "View Project Timeline", description: "Access timeline/Gantt" },
        view_project_financials: { label: "View Project Financials", description: "See financial summaries" },
        view_project_payments: { label: "View Payment Schedule", description: "See payment details" },
        view_project_crew: { label: "View Crew Assignments", description: "See assigned crew" },
        update_milestone_progress: { label: "Update Milestone Progress", description: "Mark milestones complete" },
        add_project_milestones: { label: "Add New Milestones", description: "Create project milestones" },
        edit_project_milestones: { label: "Edit Milestones", description: "Modify milestone details" },
        delete_project_milestones: { label: "Delete Milestones", description: "Remove milestones" },
        add_calendar_events: { label: "Add Calendar Events", description: "Schedule project events" },
        assign_crew_to_project: { label: "Assign Crew to Projects", description: "Manage crew assignments" },
      }
    },
    financials: {
      label: "Financial Operations",
      description: "Control financial actions and visibility",
      permissions: {
        submit_costs: { label: "Submit Costs", description: "Add project costs" },
        approve_costs: { label: "Approve Costs", description: "Approve submitted costs" },
        delete_costs: { label: "Delete Costs", description: "Remove cost entries" },
        view_cost_breakdown: { label: "View Cost Breakdown", description: "See detailed cost analysis" },
        view_profit_margins: { label: "View Profit Margins", description: "See margin percentages" },
        generate_invoice: { label: "Generate Invoices", description: "Create project invoices" },
        send_invoice: { label: "Send Invoices to Clients", description: "Email invoices" },
        mark_payment_received: { label: "Mark Payments as Received", description: "Record client payments" },
        view_job_costing_simple: { label: "View Job Costing (Simple)", description: "Basic cost view" },
        view_job_costing_detailed: { label: "View Job Costing (Detailed)", description: "Full cost breakdown" },
        view_company_financials: { label: "View Company Financials", description: "Company-wide financial data" },
        manage_payment_schedule: { label: "Manage Payment Schedules", description: "Edit payment terms" },
      }
    },
    leads_clients: {
      label: "Leads & Clients",
      description: "Control lead and client management",
      permissions: {
        create_lead: { label: "Create New Leads", description: "Add new leads" },
        edit_lead: { label: "Edit Lead Details", description: "Modify lead information" },
        delete_lead: { label: "Delete Leads", description: "Remove leads" },
        convert_lead_to_client: { label: "Convert Lead to Client", description: "Qualify leads" },
        view_client_details: { label: "View Client Details", description: "See client information" },
        edit_client: { label: "Edit Client Details", description: "Modify client info" },
        view_client_history: { label: "View Client History", description: "See past projects/estimates" },
        send_client_communications: { label: "Send Client Communications", description: "Email/SMS clients" },
      }
    },
    subcontractors: {
      label: "Subcontractor Management",
      description: "Control subcontractor operations",
      permissions: {
        view_subcontractors: { label: "View Subcontractor List", description: "See all subcontractors" },
        add_subcontractor: { label: "Add New Subcontractors", description: "Create subcontractor profiles" },
        edit_subcontractor: { label: "Edit Subcontractor Details", description: "Modify sub information" },
        delete_subcontractor: { label: "Delete Subcontractors", description: "Remove subcontractors" },
        view_sub_rates: { label: "View Subcontractor Rates", description: "See pricing information" },
        send_job_offers: { label: "Send Job Offers", description: "Send work requests to subs" },
        view_sub_portal: { label: "Access Sub Portal Data", description: "See subcontractor submissions" },
        approve_sub_work: { label: "Approve Subcontractor Work", description: "Approve completed work" },
      }
    },
    field_operations: {
      label: "Field Operations",
      description: "Control field work and site visits",
      permissions: {
        schedule_field_visit: { label: "Schedule Field Visits", description: "Book site visits" },
        conduct_walkthrough: { label: "Conduct Walkthroughs", description: "Perform site walkthroughs" },
        capture_field_photos: { label: "Capture Field Photos", description: "Take and upload photos" },
        submit_field_reports: { label: "Submit Field Reports", description: "Complete field reports" },
        view_field_history: { label: "View Field History", description: "See past visits" },
        log_time_entries: { label: "Log Time Entries", description: "Record work time" },
        approve_time_entries: { label: "Approve Time Entries", description: "Approve team time logs" },
        view_all_time_entries: { label: "View All Time Entries", description: "See team time data" },
      }
    },
    purchase_orders: {
      label: "Purchase Orders & Materials",
      description: "Control purchasing and materials",
      permissions: {
        create_purchase_order: { label: "Create Purchase Orders", description: "Generate new POs" },
        edit_purchase_order: { label: "Edit Purchase Orders", description: "Modify PO details" },
        approve_purchase_order: { label: "Approve Purchase Orders", description: "Authorize purchases" },
        delete_purchase_order: { label: "Delete Purchase Orders", description: "Remove POs" },
        receive_materials: { label: "Receive Materials", description: "Mark materials as received" },
        view_supplier_pricing: { label: "View Supplier Pricing", description: "See supplier costs" },
      }
    },
    team_admin: {
      label: "Team & Administration",
      description: "Control team and system settings",
      permissions: {
        view_team_members: { label: "View Team Members", description: "See team list" },
        add_team_members: { label: "Add Team Members", description: "Invite new users" },
        edit_team_members: { label: "Edit Team Members", description: "Modify user details" },
        remove_team_members: { label: "Remove Team Members", description: "Delete users" },
        manage_roles: { label: "Manage Roles & Permissions", description: "Configure role permissions" },
        view_audit_log: { label: "View Audit Log", description: "See activity history" },
        manage_company_settings: { label: "Manage Company Settings", description: "Edit company profile" },
        manage_integrations: { label: "Manage Integrations", description: "Configure third-party connections" },
        export_company_data: { label: "Export Company Data", description: "Download company data" },
        manage_billing: { label: "Manage Billing/Subscription", description: "Handle payments" },
      }
    },
    ai_features: {
      label: "AI & Advanced Features",
      description: "Control AI-powered capabilities",
      permissions: {
        use_ai_takeoff: { label: "Use AI Blueprint Takeoff", description: "AI-powered measurements" },
        use_ai_estimating_rules: { label: "Configure AI Estimating Rules", description: "Set AI parameters" },
        use_ai_suggestions: { label: "Use AI Suggestions", description: "Get AI recommendations" },
        view_ai_analytics: { label: "View AI Analytics", description: "See AI performance data" },
      }
    }
  };
  
  // Available roles - Comprehensive construction industry roles
  const roles = [
    // Executive/Leadership
    { id: "owner", label: "Owner", description: "Company owner - full access to everything", color: "bg-amber-500", category: "executive" },
    { id: "ceo", label: "CEO", description: "Chief Executive Officer - strategic oversight", color: "bg-amber-600", category: "executive" },
    { id: "coo", label: "COO", description: "Chief Operating Officer - operations oversight", color: "bg-amber-700", category: "executive" },
    { id: "cfo", label: "CFO", description: "Chief Financial Officer - financial oversight", color: "bg-amber-800", category: "executive" },
    { id: "president", label: "President", description: "Company President - executive leadership", color: "bg-yellow-600", category: "executive" },
    { id: "vp", label: "Vice President", description: "VP - senior management", color: "bg-yellow-700", category: "executive" },
    
    // Operations Management
    { id: "general_manager", label: "General Manager", description: "Overall operations management", color: "bg-purple-500", category: "management" },
    { id: "operations_manager", label: "Operations Manager", description: "Day-to-day operations", color: "bg-purple-600", category: "management" },
    { id: "construction_manager", label: "Construction Manager", description: "Construction operations oversight", color: "bg-purple-700", category: "management" },
    { id: "pm", label: "Project Manager", description: "Project planning and execution", color: "bg-blue-500", category: "management" },
    { id: "assistant_pm", label: "Assistant PM", description: "Project manager support", color: "bg-blue-600", category: "management" },
    
    // Site Leadership
    { id: "superintendent", label: "Superintendent", description: "Multi-site supervision", color: "bg-indigo-500", category: "site" },
    { id: "site_supervisor", label: "Site Supervisor", description: "On-site supervision and coordination", color: "bg-indigo-600", category: "site" },
    { id: "foreman", label: "Foreman", description: "Crew leader and task management", color: "bg-indigo-700", category: "site" },
    { id: "lead_carpenter", label: "Lead Carpenter", description: "Senior carpenter / trade lead", color: "bg-indigo-800", category: "site" },
    
    // Administrative
    { id: "admin", label: "Admin", description: "System administrator", color: "bg-rose-500", category: "admin" },
    { id: "office_manager", label: "Office Manager", description: "Office operations management", color: "bg-rose-600", category: "admin" },
    { id: "office", label: "Office Staff", description: "General office support", color: "bg-emerald-500", category: "admin" },
    { id: "receptionist", label: "Receptionist", description: "Front desk and phone support", color: "bg-emerald-600", category: "admin" },
    { id: "admin_assistant", label: "Admin Assistant", description: "Administrative support", color: "bg-emerald-700", category: "admin" },
    
    // Finance & Accounting
    { id: "controller", label: "Controller", description: "Financial controller - accounting oversight", color: "bg-green-500", category: "finance" },
    { id: "bookkeeper", label: "Bookkeeper", description: "Daily financial records", color: "bg-green-600", category: "finance" },
    { id: "accountant", label: "Accountant", description: "Financial reporting and analysis", color: "bg-green-700", category: "finance" },
    { id: "ap_clerk", label: "AP Clerk", description: "Accounts payable processing", color: "bg-green-800", category: "finance" },
    { id: "ar_clerk", label: "AR Clerk", description: "Accounts receivable processing", color: "bg-lime-600", category: "finance" },
    { id: "payroll", label: "Payroll Specialist", description: "Payroll processing", color: "bg-lime-700", category: "finance" },
    
    // Sales & Business Development
    { id: "sales_manager", label: "Sales Manager", description: "Sales team management", color: "bg-cyan-500", category: "sales" },
    { id: "sales_rep", label: "Sales Rep", description: "Client acquisition and sales", color: "bg-cyan-600", category: "sales" },
    { id: "business_dev", label: "Business Development", description: "New business opportunities", color: "bg-cyan-700", category: "sales" },
    { id: "estimator", label: "Estimator", description: "Project estimation and bidding", color: "bg-teal-500", category: "sales" },
    { id: "senior_estimator", label: "Senior Estimator", description: "Complex estimates and bid review", color: "bg-teal-600", category: "sales" },
    { id: "bid_manager", label: "Bid Manager", description: "Bid coordination and submission", color: "bg-teal-700", category: "sales" },
    
    // Field Operations
    { id: "field", label: "Field Crew", description: "General field workers", color: "bg-orange-500", category: "field" },
    { id: "journeyman", label: "Journeyman", description: "Skilled tradesperson", color: "bg-orange-600", category: "field" },
    { id: "apprentice", label: "Apprentice", description: "Trade apprentice in training", color: "bg-orange-700", category: "field" },
    { id: "laborer", label: "Laborer", description: "General labor support", color: "bg-orange-800", category: "field" },
    { id: "helper", label: "Helper", description: "Trade helper / assistant", color: "bg-red-600", category: "field" },
    
    // Safety & Quality
    { id: "safety_manager", label: "Safety Manager", description: "Safety program management", color: "bg-red-500", category: "safety" },
    { id: "safety_officer", label: "Safety Officer", description: "On-site safety compliance", color: "bg-red-600", category: "safety" },
    { id: "qc_manager", label: "QC Manager", description: "Quality control management", color: "bg-red-700", category: "safety" },
    { id: "qc_inspector", label: "QC Inspector", description: "Quality inspections", color: "bg-red-800", category: "safety" },
    
    // Purchasing & Warehouse
    { id: "purchasing_manager", label: "Purchasing Manager", description: "Procurement management", color: "bg-violet-500", category: "purchasing" },
    { id: "purchasing_agent", label: "Purchasing Agent", description: "Material purchasing", color: "bg-violet-600", category: "purchasing" },
    { id: "warehouse_manager", label: "Warehouse Manager", description: "Inventory management", color: "bg-violet-700", category: "purchasing" },
    { id: "warehouse_staff", label: "Warehouse Staff", description: "Warehouse operations", color: "bg-violet-800", category: "purchasing" },
    { id: "equipment_manager", label: "Equipment Manager", description: "Equipment and fleet management", color: "bg-fuchsia-600", category: "purchasing" },
    
    // HR & Support
    { id: "hr_manager", label: "HR Manager", description: "Human resources management", color: "bg-pink-500", category: "hr" },
    { id: "hr_coordinator", label: "HR Coordinator", description: "HR support and coordination", color: "bg-pink-600", category: "hr" },
    { id: "recruiter", label: "Recruiter", description: "Talent acquisition", color: "bg-pink-700", category: "hr" },
    { id: "training_coordinator", label: "Training Coordinator", description: "Employee training programs", color: "bg-pink-800", category: "hr" },
    
    // IT & Marketing
    { id: "it_admin", label: "IT Admin", description: "Technology and systems", color: "bg-slate-500", category: "support" },
    { id: "marketing_manager", label: "Marketing Manager", description: "Marketing and branding", color: "bg-slate-600", category: "support" },
    
    // External/Limited
    { id: "client_viewer", label: "Client Viewer", description: "Client portal access only", color: "bg-gray-500", category: "external" },
    { id: "subcontractor", label: "Subcontractor", description: "Subcontractor portal access", color: "bg-gray-600", category: "external" },
    { id: "vendor", label: "Vendor", description: "Vendor/supplier access", color: "bg-gray-700", category: "external" },
    { id: "consultant", label: "Consultant", description: "External consultant access", color: "bg-gray-800", category: "external" },
  ];
  
  // Role categories for grouping in UI
  const roleCategories = [
    { id: "executive", label: "Executive / Leadership", icon: "👔" },
    { id: "management", label: "Operations Management", icon: "📋" },
    { id: "site", label: "Site Leadership", icon: "🏗️" },
    { id: "admin", label: "Administrative", icon: "🖥️" },
    { id: "finance", label: "Finance & Accounting", icon: "💰" },
    { id: "sales", label: "Sales & Estimating", icon: "📊" },
    { id: "field", label: "Field Operations", icon: "🔨" },
    { id: "safety", label: "Safety & Quality", icon: "🦺" },
    { id: "purchasing", label: "Purchasing & Warehouse", icon: "📦" },
    { id: "hr", label: "HR & Training", icon: "👥" },
    { id: "support", label: "IT & Marketing", icon: "💻" },
    { id: "external", label: "External / Limited", icon: "🔒" },
  ];
  
  // Generate default permissions based on role type
  const generateDefaultPermissions = () => {
    const defaults = {};
    roles.forEach(role => {
      defaults[role.id] = {};
      Object.entries(permissionCategories).forEach(([catKey, category]) => {
        Object.keys(category.permissions).forEach(permKey => {
          // Executive roles get everything
          if (['owner', 'ceo', 'coo', 'president', 'vp', 'general_manager'].includes(role.id)) {
            defaults[role.id][permKey] = true;
          }
          // CFO gets financial focus
          else if (role.id === 'cfo' || role.id === 'controller') {
            defaults[role.id][permKey] = permKey.includes('view_') || permKey.includes('financial') || permKey.includes('cost') || permKey.includes('invoice') || permKey.includes('payment') || permKey.includes('report') || permKey.includes('billing') || catKey === 'financials';
          }
          // Admin gets system access
          else if (role.id === 'admin' || role.id === 'it_admin') {
            defaults[role.id][permKey] = true;
          }
          // Operations/Construction managers
          else if (['operations_manager', 'construction_manager'].includes(role.id)) {
            defaults[role.id][permKey] = !permKey.includes('billing') && !permKey.includes('manage_roles') && !permKey.includes('subscription');
          }
          // Project managers and assistants
          else if (['pm', 'assistant_pm'].includes(role.id)) {
            defaults[role.id][permKey] = !permKey.includes('billing') && !permKey.includes('manage_roles') && !permKey.includes('remove_team') && !permKey.includes('subscription') && !permKey.includes('company_');
          }
          // Site leadership
          else if (['superintendent', 'site_supervisor', 'foreman', 'lead_carpenter'].includes(role.id)) {
            defaults[role.id][permKey] = permKey.includes('view_dashboard') || permKey.includes('view_projects') || permKey.includes('view_project_') || permKey.includes('field') || permKey.includes('time') || permKey.includes('progress') || permKey.includes('photo') || permKey.includes('milestone') || permKey.includes('crew') || permKey.includes('calendar') || permKey.includes('schedule');
          }
          // Office management
          else if (['office_manager'].includes(role.id)) {
            defaults[role.id][permKey] = permKey.includes('view_') || permKey.includes('create_') || permKey.includes('edit_') || permKey.includes('submit_') || !permKey.includes('delete_') && !permKey.includes('manage_roles') && !permKey.includes('billing');
          }
          // Office staff
          else if (['office', 'receptionist', 'admin_assistant'].includes(role.id)) {
            defaults[role.id][permKey] = permKey.includes('view_') || permKey.includes('submit_') || permKey.includes('create_lead') || permKey.includes('schedule');
          }
          // Finance roles
          else if (['bookkeeper', 'accountant', 'ap_clerk', 'ar_clerk', 'payroll'].includes(role.id)) {
            defaults[role.id][permKey] = permKey.includes('view_') || permKey.includes('cost') || permKey.includes('invoice') || permKey.includes('payment') || permKey.includes('financial') || catKey === 'financials';
          }
          // Sales management
          else if (['sales_manager', 'business_dev'].includes(role.id)) {
            defaults[role.id][permKey] = permKey.includes('view_') || permKey.includes('lead') || permKey.includes('client') || permKey.includes('proposal') || permKey.includes('estimate') || permKey.includes('send_');
          }
          // Sales reps
          else if (['sales_rep'].includes(role.id)) {
            defaults[role.id][permKey] = permKey.includes('view_dashboard') || permKey.includes('view_leads') || permKey.includes('view_clients') || permKey.includes('lead') || permKey.includes('client') || permKey.includes('create_lead') || permKey.includes('edit_lead') || permKey.includes('schedule');
          }
          // Estimators
          else if (['estimator', 'senior_estimator', 'bid_manager'].includes(role.id)) {
            defaults[role.id][permKey] = permKey.includes('estimate') || permKey.includes('proposal') || permKey.includes('view_') || permKey.includes('ai_') || permKey.includes('takeoff') || permKey.includes('supplier');
          }
          // Field workers
          else if (['field', 'journeyman', 'apprentice', 'laborer', 'helper'].includes(role.id)) {
            defaults[role.id][permKey] = permKey.includes('view_dashboard') || permKey.includes('view_projects') || permKey.includes('field') || permKey.includes('time') || permKey.includes('log_time') || permKey.includes('progress') || permKey.includes('photo') || permKey === 'submit_costs';
          }
          // Safety roles
          else if (['safety_manager', 'safety_officer'].includes(role.id)) {
            defaults[role.id][permKey] = permKey.includes('view_') || permKey.includes('field') || permKey.includes('report') || permKey.includes('photo') || permKey.includes('walkthrough');
          }
          // QC roles
          else if (['qc_manager', 'qc_inspector'].includes(role.id)) {
            defaults[role.id][permKey] = permKey.includes('view_') || permKey.includes('field') || permKey.includes('report') || permKey.includes('photo') || permKey.includes('walkthrough') || permKey.includes('approve_');
          }
          // Purchasing roles
          else if (['purchasing_manager', 'purchasing_agent'].includes(role.id)) {
            defaults[role.id][permKey] = permKey.includes('view_') || permKey.includes('purchase') || permKey.includes('supplier') || permKey.includes('cost') || permKey.includes('material');
          }
          // Warehouse roles
          else if (['warehouse_manager', 'warehouse_staff', 'equipment_manager'].includes(role.id)) {
            defaults[role.id][permKey] = permKey.includes('view_dashboard') || permKey.includes('view_purchase') || permKey.includes('receive_') || permKey.includes('material') || permKey.includes('equipment');
          }
          // HR roles
          else if (['hr_manager', 'hr_coordinator', 'recruiter', 'training_coordinator'].includes(role.id)) {
            defaults[role.id][permKey] = permKey.includes('view_team') || permKey.includes('view_dashboard') || permKey.includes('team_') || permKey.includes('add_team') || permKey.includes('edit_team') || permKey.includes('time');
          }
          // Marketing
          else if (['marketing_manager'].includes(role.id)) {
            defaults[role.id][permKey] = permKey.includes('view_') || permKey.includes('proposal') || permKey.includes('client') || permKey.includes('lead');
          }
          // External/Limited roles
          else if (['client_viewer'].includes(role.id)) {
            defaults[role.id][permKey] = permKey === 'view_dashboard';
          }
          else if (['subcontractor'].includes(role.id)) {
            defaults[role.id][permKey] = permKey.includes('view_dashboard') || permKey === 'submit_costs' || permKey === 'log_time_entries' || permKey === 'capture_field_photos';
          }
          else if (['vendor'].includes(role.id)) {
            defaults[role.id][permKey] = permKey === 'view_dashboard' || permKey === 'view_purchase_orders';
          }
          else if (['consultant'].includes(role.id)) {
            defaults[role.id][permKey] = permKey.includes('view_');
          }
          else {
            defaults[role.id][permKey] = false;
          }
        });
      });
    });
    return defaults;
  };

  const [permissions, setPermissions] = useState(generateDefaultPermissions());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({ 
    navigation: true, 
    estimates: true, 
    proposals: true, 
    projects: true, 
    financials: true,
    leads_clients: true,
    time_tracking: true,
    documents: true,
    team: true,
    settings: true
  });
  const [selectedRole, setSelectedRole] = useState('owner'); // Default to owner role
  const [searchTerm, setSearchTerm] = useState('');
  const permissionsSectionRef = React.useRef(null);

  const handleRoleSelect = (roleId) => {
    setSelectedRole(roleId);
    // Scroll to permissions section after role selection
    setTimeout(() => {
      permissionsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const response = await API.get("/settings/permissions");
        if (response.data && typeof response.data === 'object') {
          // Merge API response with defaults
          const defaults = generateDefaultPermissions();
          const merged = { ...defaults };
          Object.keys(response.data).forEach(roleId => {
            if (merged[roleId]) {
              merged[roleId] = { ...merged[roleId], ...response.data[roleId] };
            }
          });
          setPermissions(merged);
        }
      } catch (error) {
        console.error("Failed to load permissions", error);
      } finally {
        setLoading(false);
      }
    };
    if (isAdmin) {
      fetchPermissions();
    } else {
      setLoading(false);
    }
  }, [isAdmin]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await API.put("/settings/permissions", permissions);
      toast.success("Permissions saved successfully!");
    } catch (error) {
      toast.error("Failed to save permissions");
    } finally {
      setSaving(false);
    }
  };

  const toggleCategory = (catKey) => {
    setExpandedCategories(prev => ({ ...prev, [catKey]: !prev[catKey] }));
  };

  const togglePermission = (roleId, permKey) => {
    setPermissions(prev => ({
      ...prev,
      [roleId]: {
        ...(prev[roleId] || {}),
        [permKey]: !(prev[roleId]?.[permKey] ?? false)
      }
    }));
  };

  const toggleAllInCategory = (catKey, roleId, value) => {
    const category = permissionCategories[catKey];
    const updates = {};
    Object.keys(category.permissions).forEach(permKey => {
      updates[permKey] = value;
    });
    setPermissions(prev => ({
      ...prev,
      [roleId]: { ...(prev[roleId] || {}), ...updates }
    }));
  };

  const getCategoryStats = (catKey, roleId) => {
    const category = permissionCategories[catKey];
    const permKeys = Object.keys(category.permissions);
    const enabled = permKeys.filter(k => permissions[roleId]?.[k]).length;
    return { enabled, total: permKeys.length };
  };

  // Filter permissions based on search
  const filterPermissions = (perms) => {
    if (!searchTerm) return perms;
    const filtered = {};
    Object.entries(perms).forEach(([key, value]) => {
      if (value.label.toLowerCase().includes(searchTerm.toLowerCase()) || 
          value.description.toLowerCase().includes(searchTerm.toLowerCase())) {
        filtered[key] = value;
      }
    });
    return filtered;
  };

  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <Lock className="h-12 w-12 mx-auto text-slate-300 mb-4" />
        <p className="text-slate-400">Admin access required to manage permissions</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500 mr-3" />
        <span className="text-slate-400">Loading permissions...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-chivo font-bold text-white">Roles & Permissions</h2>
          <p className="text-slate-400 text-sm mt-1">Configure granular access control for each role across the entire application</p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving} 
          className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
          {saving ? "Saving..." : "Save All Permissions"}
        </Button>
      </div>

      {/* Role Selector - Grouped by Category */}
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-bold text-white">Select Role to Configure</h3>
              <p className="text-xs text-slate-500">{roles.length} roles across {roleCategories.length} categories</p>
            </div>
            <Badge className="bg-emerald-500/20 text-emerald-400">
              {Object.values(permissions[selectedRole] || {}).filter(Boolean).length} permissions enabled
            </Badge>
          </div>
          
          <div className="space-y-4">
            {roleCategories.map(category => {
              const categoryRoles = roles.filter(r => r.category === category.id);
              if (categoryRoles.length === 0) return null;
              
              return (
                <div key={category.id}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg">{category.icon}</span>
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{category.label}</span>
                    <span className="text-xs text-slate-600">({categoryRoles.length})</span>
                  </div>
                  <div className="flex gap-2 flex-wrap pl-6">
                    {categoryRoles.map(role => (
                      <button
                        key={role.id}
                        onClick={() => handleRoleSelect(role.id)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
                          selectedRole === role.id
                            ? `${role.color} text-white shadow-lg`
                            : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white'
                        }`}
                        title={role.description}
                      >
                        <Shield className="h-3 w-3" />
                        {role.label}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Selected Role Info */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${roles.find(r => r.id === selectedRole)?.color}`}>
                <Shield className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-white">{roles.find(r => r.id === selectedRole)?.label}</h3>
                <p className="text-sm text-slate-400">{roles.find(r => r.id === selectedRole)?.description}</p>
                <p className="text-xs text-slate-600 mt-0.5">Category: {roleCategories.find(c => c.id === roles.find(r => r.id === selectedRole)?.category)?.label}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-emerald-400">
                {Object.values(permissions[selectedRole] || {}).filter(Boolean).length}
              </div>
              <div className="text-xs text-slate-500">of {Object.keys(permissions[selectedRole] || {}).length} permissions</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search and Expand/Collapse Controls */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search permissions..."
            className="w-full px-4 py-3 pl-10 rounded-lg bg-slate-800 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
          />
          <Eye className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
        </div>
        <Button
          variant="outline"
          onClick={() => {
            const allExpanded = Object.keys(permissionCategories).every(k => expandedCategories[k]);
            const newState = {};
            Object.keys(permissionCategories).forEach(k => { newState[k] = !allExpanded; });
            setExpandedCategories(newState);
          }}
          className="border-slate-700 text-slate-300 hover:bg-slate-800 whitespace-nowrap"
        >
          {Object.keys(permissionCategories).every(k => expandedCategories[k]) ? (
            <><ChevronUp className="h-4 w-4 mr-2" />Collapse All</>
          ) : (
            <><ChevronDown className="h-4 w-4 mr-2" />Expand All</>
          )}
        </Button>
      </div>

      {/* Permission Categories */}
      <div ref={permissionsSectionRef} className="space-y-4">
        {Object.entries(permissionCategories).map(([catKey, category]) => {
          const filteredPerms = filterPermissions(category.permissions);
          if (searchTerm && Object.keys(filteredPerms).length === 0) return null;
          
          const stats = getCategoryStats(catKey, selectedRole);
          const isExpanded = expandedCategories[catKey];
          const allEnabled = stats.enabled === stats.total;
          const noneEnabled = stats.enabled === 0;
          
          return (
            <Card key={catKey} className="bg-slate-900 border-slate-800 overflow-hidden">
              <div 
                className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-slate-800/50 transition-colors"
                onClick={() => toggleCategory(catKey)}
              >
                <div className="flex items-center gap-3">
                  {isExpanded ? <ChevronDown className="h-5 w-5 text-slate-400" /> : <ChevronRight className="h-5 w-5 text-slate-400" />}
                  <div>
                    <h3 className="font-bold text-white">{category.label}</h3>
                    <p className="text-xs text-slate-500">{category.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className={`text-sm font-medium px-2 py-1 rounded ${
                    allEnabled ? 'bg-emerald-500/20 text-emerald-400' : 
                    noneEnabled ? 'bg-slate-700 text-slate-400' : 
                    'bg-amber-500/20 text-amber-400'
                  }`}>
                    {stats.enabled}/{stats.total}
                  </div>
                  <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => toggleAllInCategory(catKey, selectedRole, true)}
                      className="px-2 py-1 text-xs rounded bg-emerald-600/20 text-emerald-400 hover:bg-emerald-600/30 transition-colors"
                    >
                      All On
                    </button>
                    <button
                      onClick={() => toggleAllInCategory(catKey, selectedRole, false)}
                      className="px-2 py-1 text-xs rounded bg-red-600/20 text-red-400 hover:bg-red-600/30 transition-colors"
                    >
                      All Off
                    </button>
                  </div>
                </div>
              </div>
              
              {isExpanded && (
                <CardContent className="pt-0 pb-4 border-t border-slate-800">
                  <div className="grid gap-2 mt-3">
                    {Object.entries(searchTerm ? filteredPerms : category.permissions).map(([permKey, perm]) => {
                      const isEnabled = permissions[selectedRole]?.[permKey] ?? false;
                      return (
                        <div 
                          key={permKey}
                          className={`flex items-center justify-between p-3 rounded-lg transition-all ${
                            isEnabled ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-slate-800/50 border border-slate-700/50'
                          }`}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className={`font-medium ${isEnabled ? 'text-white' : 'text-slate-400'}`}>
                                {perm.label}
                              </span>
                              {isEnabled && <Check className="h-4 w-4 text-emerald-400" />}
                            </div>
                            <p className="text-xs text-slate-500 mt-0.5">{perm.description}</p>
                          </div>
                          <Switch
                            checked={isEnabled}
                            onCheckedChange={() => togglePermission(selectedRole, permKey)}
                          />
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {/* Quick Actions */}
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="pt-6">
          <h3 className="font-bold text-white mb-4">Quick Actions</h3>
          <div className="flex gap-3 flex-wrap">
            <Button
              variant="outline"
              onClick={() => {
                const defaults = generateDefaultPermissions();
                setPermissions(prev => ({ ...prev, [selectedRole]: defaults[selectedRole] }));
                toast.info(`Reset ${roles.find(r => r.id === selectedRole)?.label} to defaults`);
              }}
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Reset to Defaults
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                const allOn = {};
                Object.values(permissionCategories).forEach(cat => {
                  Object.keys(cat.permissions).forEach(k => allOn[k] = true);
                });
                setPermissions(prev => ({ ...prev, [selectedRole]: allOn }));
                toast.info(`Enabled all permissions for ${roles.find(r => r.id === selectedRole)?.label}`);
              }}
              className="border-emerald-700 text-emerald-400 hover:bg-emerald-900/30"
            >
              <Check className="h-4 w-4 mr-2" />
              Enable All
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                const allOff = {};
                Object.values(permissionCategories).forEach(cat => {
                  Object.keys(cat.permissions).forEach(k => allOff[k] = false);
                });
                setPermissions(prev => ({ ...prev, [selectedRole]: allOff }));
                toast.info(`Disabled all permissions for ${roles.find(r => r.id === selectedRole)?.label}`);
              }}
              className="border-red-700 text-red-400 hover:bg-red-900/30"
            >
              <X className="h-4 w-4 mr-2" />
              Disable All
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Save Footer */}
      <div className="sticky bottom-0 bg-slate-950/90 backdrop-blur-sm py-4 border-t border-slate-800 -mx-6 px-6 flex justify-end">
        <Button 
          onClick={handleSave} 
          disabled={saving} 
          size="lg"
          className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save size={18} className="mr-2" />}
          {saving ? "Saving Permissions..." : "Save All Changes"}
        </Button>
      </div>
    </div>
  );
};

// ================== ESTIMATING & AI SECTION ==================

const SupplierPricing = () => {
  const { settings, updateSettings } = useSettings();
  const [form, setForm] = useState({
    supplier_pricing_enabled: settings?.supplier_pricing_enabled ?? true,
    preferred_supplier: settings?.preferred_supplier || "lowes",
    pricing_refresh: settings?.pricing_refresh || "weekly",
    location_pricing: settings?.location_pricing ?? false,
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await updateSettings(form);
    setSaving(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-chivo font-bold text-white">Supplier Pricing</h2>
        <p className="text-slate-400 text-sm mt-1">Configure material pricing sources</p>
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="pt-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-white">Enable Supplier Pricing</p>
              <p className="text-sm text-slate-400">Use real-time pricing from suppliers</p>
            </div>
            <Switch
              checked={form.supplier_pricing_enabled}
              onCheckedChange={(checked) => setForm({ ...form, supplier_pricing_enabled: checked })}
            />
          </div>

          {form.supplier_pricing_enabled && (
            <>
              <div>
                <label className="text-sm font-medium text-slate-300 mb-2 block">Preferred Supplier</label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { id: "lowes", label: "Lowe's", icon: "🏪" },
                    { id: "homedepot", label: "Home Depot", icon: "🏠" },
                    { id: "other", label: "Other", icon: "📦" },
                  ].map((supplier) => (
                    <button
                      key={supplier.id}
                      onClick={() => setForm({ ...form, preferred_supplier: supplier.id })}
                      className={`p-4 rounded-lg border-2 text-center transition-all ${
                        form.preferred_supplier === supplier.id
                          ? "border-emerald-500 bg-emerald-500/10"
                          : "border-slate-700 hover:border-slate-300"
                      }`}
                    >
                      <span className="text-2xl">{supplier.icon}</span>
                      <p className="font-medium text-white mt-1">{supplier.label}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-300 mb-1 block">Refresh Frequency</label>
                <Select value={form.pricing_refresh} onValueChange={(v) => setForm({ ...form, pricing_refresh: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="daily" className="text-slate-200">Daily</SelectItem>
                    <SelectItem value="weekly" className="text-slate-200">Weekly</SelectItem>
                    <SelectItem value="monthly" className="text-slate-200">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-slate-800">
                <div>
                  <p className="font-medium text-white">Location-Based Pricing</p>
                  <p className="text-sm text-slate-400">Adjust prices based on project ZIP code</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-slate-700 text-slate-300">Coming Soon</Badge>
                  <Switch checked={form.location_pricing} disabled />
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">
          <Save size={16} className="mr-2" />
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </div>
  );
};

const Catalogs = () => {
  const [catalogs, setCatalogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newCatalog, setNewCatalog] = useState({ name: "", supplier: "lowes" });

  useEffect(() => {
    const fetchCatalogs = async () => {
      try {
        const response = await API.get("/settings/catalogs");
        setCatalogs(response.data);
      } catch (error) {
        console.error("Failed to load catalogs", error);
      } finally {
        setLoading(false);
      }
    };
    fetchCatalogs();
  }, []);

  const handleAddCatalog = async () => {
    try {
      const response = await API.post("/settings/catalogs", newCatalog);
      setCatalogs([...catalogs, response.data]);
      setAddDialogOpen(false);
      setNewCatalog({ name: "", supplier: "lowes" });
      toast.success("Catalog created");
    } catch (error) {
      toast.error("Failed to create catalog");
    }
  };

  const handleDeleteCatalog = async (catalogId) => {
    try {
      await API.delete(`/settings/catalogs/${catalogId}`);
      setCatalogs(catalogs.filter(c => c.catalog_id !== catalogId));
      toast.success("Catalog deleted");
    } catch (error) {
      toast.error("Failed to delete catalog");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-chivo font-bold text-white">Catalogs</h2>
          <p className="text-slate-400 text-sm mt-1">Manage your material and pricing catalogs</p>
        </div>
        <Button onClick={() => setAddDialogOpen(true)} className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">
          <Plus size={16} className="mr-2" />
          New Catalog
        </Button>
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="pt-6">
          {loading ? (
            <div className="text-center py-8 text-slate-400">Loading catalogs...</div>
          ) : catalogs.length === 0 ? (
            <div className="text-center py-8">
              <BookOpen className="h-10 w-10 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-400">No catalogs yet</p>
              <Button className="mt-3 bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold" onClick={() => setAddDialogOpen(true)}>
                Add First Catalog
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {catalogs.map((catalog) => (
                <div key={catalog.catalog_id} className="flex items-center justify-between p-4 bg-slate-900 rounded-lg border border-slate-800">
                  <div className="flex items-center gap-3">
                    <BookOpen className="h-5 w-5 text-slate-400" />
                    <div>
                      <p className="font-medium text-white">{catalog.name}</p>
                      <p className="text-sm text-slate-400">Source: {catalog.supplier} · {catalog.item_count || 0} items</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={catalog.status === "active" ? "default" : "secondary"}>
                      {catalog.status}
                    </Badge>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteCatalog(catalog.catalog_id)}>
                      <Trash2 size={14} className="text-slate-400" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Catalog Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">Add New Catalog</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-300 mb-1 block">Catalog Name</label>
              <Input
                value={newCatalog.name}
                onChange={(e) => setNewCatalog({ ...newCatalog, name: e.target.value })}
                placeholder="e.g., Standard Materials"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300 mb-1 block">Supplier</label>
              <Select value={newCatalog.supplier} onValueChange={(v) => setNewCatalog({ ...newCatalog, supplier: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lowes">Lowes</SelectItem>
                  <SelectItem value="homedepot">Home Depot</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAddDialogOpen(false)} className="border-slate-700 text-slate-300 hover:bg-slate-800">Cancel</Button>
              <Button onClick={handleAddCatalog} disabled={!newCatalog.name} className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">Add Catalog</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const EstimatingRules = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newRule, setNewRule] = useState({ text: "", scope: "all" });

  useEffect(() => {
    const fetchRules = async () => {
      try {
        const response = await API.get("/settings/estimating-rules");
        setRules(response.data);
      } catch (error) {
        console.error("Failed to load rules", error);
      } finally {
        setLoading(false);
      }
    };
    fetchRules();
  }, []);

  const handleAddRule = async () => {
    try {
      const response = await API.post("/settings/estimating-rules", newRule);
      setRules([...rules, response.data]);
      setAddDialogOpen(false);
      setNewRule({ text: "", scope: "all" });
      toast.success("Rule added");
    } catch (error) {
      toast.error("Failed to add rule");
    }
  };

  const handleDeleteRule = async (ruleId) => {
    try {
      await API.delete(`/settings/estimating-rules/${ruleId}`);
      setRules(rules.filter(r => r.rule_id !== ruleId));
      toast.success("Rule deleted");
    } catch (error) {
      toast.error("Failed to delete rule");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-chivo font-bold text-white">Estimating Rules (AI)</h2>
          <p className="text-slate-400 text-sm mt-1">Plain-language instructions for AI-assisted estimating</p>
        </div>
        <Button onClick={() => setAddDialogOpen(true)} className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">
          <Plus size={16} className="mr-2" />
          Add Rule
        </Button>
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="pt-6">
          {loading ? (
            <div className="text-center py-8 text-slate-400">Loading rules...</div>
          ) : rules.length === 0 ? (
            <div className="text-center py-8">
              <Sparkles className="h-10 w-10 mx-auto text-slate-400 mb-3" />
              <p className="text-slate-400">No estimating rules yet</p>
              <Button onClick={() => setAddDialogOpen(true)} className="mt-3 bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">
                Add First Rule
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {rules.map((rule, index) => (
                <div key={rule.rule_id} className="flex items-start gap-3 p-4 bg-slate-800 rounded-lg border border-slate-700">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <span className="text-xs font-medium text-emerald-400">{index + 1}</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-white">{rule.text}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-xs">
                        {rule.scope === "all" ? "All Estimates" : rule.scope}
                      </Badge>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => handleDeleteRule(rule.rule_id)} className="text-slate-400 hover:text-red-400 hover:bg-slate-700">
                    <Trash2 size={14} />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Rule Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">Add Estimating Rule</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-300 mb-1 block">Rule Instruction</label>
              <Textarea
                value={newRule.text}
                onChange={(e) => setNewRule({ ...newRule, text: e.target.value })}
                placeholder="e.g., Always include 10% waste factor for tile materials"
                rows={3}
              />
              <p className="text-xs text-slate-400 mt-1">Write in plain language how the AI should estimate</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300 mb-1 block">Applies To</label>
              <Select value={newRule.scope} onValueChange={(v) => setNewRule({ ...newRule, scope: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Estimates</SelectItem>
                  <SelectItem value="kitchens">Kitchens</SelectItem>
                  <SelectItem value="bathrooms">Bathrooms</SelectItem>
                  <SelectItem value="additions">Additions</SelectItem>
                  <SelectItem value="remodels">Remodels</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAddDialogOpen(false)} className="border-slate-700 text-slate-300 hover:bg-slate-800">Cancel</Button>
              <Button onClick={handleAddRule} disabled={!newRule.text} className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">Add Rule</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ================== PROFIT GUARDRAILS SECTION ==================

const ProfitGuardrails = () => {
  const [settings, setSettings] = useState({
    targetGPPerMonth: 10200,
    minimumGPPerMonth: 7000,
    maxAllowancePercent: 15,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await API.get("/settings/risk-tolerance");
        setSettings(response.data);
      } catch (error) {
        console.error("Failed to load risk settings", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await API.put("/settings/risk-tolerance", settings);
      toast.success("Risk tolerance settings saved");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="text-lg font-semibold text-white">Profit Guardrails & Risk Tolerance</h2>
        <p className="text-sm text-slate-400 mt-1">
          Configure your profitability thresholds for the Mission Threat Assessment system.
        </p>
      </div>

      {/* Traffic Light Explanation */}
      <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
        <h3 className="text-sm font-semibold text-slate-300 mb-3">How It Works</h3>
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div className="flex items-start gap-2">
            <div className="w-3 h-3 rounded-full bg-emerald-500 mt-0.5" />
            <div>
              <p className="font-medium text-emerald-700">GREEN - Mission Go</p>
              <p className="text-xs text-slate-400">GP Velocity meets target</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-3 h-3 rounded-full bg-amber-500 mt-0.5" />
            <div>
              <p className="font-medium text-amber-700">YELLOW - Risk Detected</p>
              <p className="text-xs text-slate-400">GP below target but above minimum</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500 mt-0.5" />
            <div>
              <p className="font-medium text-red-700">RED - Critical Threat</p>
              <p className="text-xs text-slate-400">GP below minimum threshold</p>
            </div>
          </div>
        </div>
      </div>

      {/* Settings Cards */}
      <div className="space-y-4">
        {/* Target GP */}
        <div className="bg-slate-900 rounded-lg border border-slate-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="block text-sm font-medium text-slate-300">
                Target Gross Profit per Month
              </label>
              <p className="text-xs text-slate-400 mt-0.5">
                This defines the GREEN threshold. Jobs meeting this are &ldquo;Mission Go&rdquo;.
              </p>
            </div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
              <input
                type="number"
                value={settings.targetGPPerMonth}
                onChange={(e) => setSettings({ ...settings, targetGPPerMonth: parseFloat(e.target.value) || 0 })}
                className="w-32 pl-7 pr-3 py-2 border border-slate-300 rounded-lg text-right text-sm focus:ring-2 focus:ring-primary/20 focus:border-emerald-500"
              />
            </div>
          </div>
        </div>

        {/* Minimum GP */}
        <div className="bg-slate-900 rounded-lg border border-slate-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="block text-sm font-medium text-slate-300">
                Minimum GP per Month
              </label>
              <p className="text-xs text-slate-400 mt-0.5">
                This defines the RED threshold. Jobs below this are &ldquo;Critical Threat&rdquo;.
              </p>
            </div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
              <input
                type="number"
                value={settings.minimumGPPerMonth}
                onChange={(e) => setSettings({ ...settings, minimumGPPerMonth: parseFloat(e.target.value) || 0 })}
                className="w-32 pl-7 pr-3 py-2 border border-slate-300 rounded-lg text-right text-sm focus:ring-2 focus:ring-primary/20 focus:border-emerald-500"
              />
            </div>
          </div>
        </div>

        {/* Max Allowance % */}
        <div className="bg-slate-900 rounded-lg border border-slate-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="block text-sm font-medium text-slate-300">
                Max Allowance Percentage
              </label>
              <p className="text-xs text-slate-400 mt-0.5">
                Defines scope risk. Higher allowances indicate undefined scope.
              </p>
            </div>
            <div className="relative">
              <input
                type="number"
                min="0"
                max="100"
                value={settings.maxAllowancePercent}
                onChange={(e) => setSettings({ ...settings, maxAllowancePercent: parseFloat(e.target.value) || 0 })}
                className="w-24 pr-8 py-2 border border-slate-300 rounded-lg text-right text-sm focus:ring-2 focus:ring-primary/20 focus:border-emerald-500"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end pt-4">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Saving...
            </>
          ) : (
            "Save Settings"
          )}
        </Button>
      </div>
    </div>
  );
};

// ================== PROJECTS & OPERATIONS SECTION ==================

const SOWDefaults = () => {
  const { settings, updateSettings } = useSettings();
  const [form, setForm] = useState({
    auto_generate_milestones: settings?.auto_generate_milestones ?? true,
    default_milestone_template: settings?.default_milestone_template || "standard",
  });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-chivo font-bold text-white">SOW Defaults</h2>
        <p className="text-slate-400 text-sm mt-1">Configure Scope of Work generation</p>
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="pt-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-white">Auto-Generate Milestones</p>
              <p className="text-sm text-slate-400">Create project milestones from SOW automatically</p>
            </div>
            <Switch
              checked={form.auto_generate_milestones}
              onCheckedChange={(checked) => setForm({ ...form, auto_generate_milestones: checked })}
            />
          </div>

          <div>
            <label className="text-sm font-medium text-slate-300 mb-2 block">Default Template</label>
            <Select value={form.default_milestone_template} onValueChange={(v) => setForm({ ...form, default_milestone_template: v })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="standard">Standard (Weekly milestones)</SelectItem>
                <SelectItem value="detailed">Detailed (Phase-based)</SelectItem>
                <SelectItem value="simple">Simple (Start/End only)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button>
          <Save size={16} className="mr-2" />
          Save Changes
        </Button>
      </div>
    </div>
  );
};

const TradePhaseMapping = () => {
  const { isAdmin } = useSettings();
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchMappings = async () => {
      try {
        const response = await API.get("/trade-phase-mappings");
        if (response.data && response.data.length > 0) {
          setMappings(response.data.map(m => ({
            trade: m.trade_phase,
            cost_code: m.cost_code || "",
            category: m.budget_category || "subcontractors"
          })));
        } else {
          // Default mappings if none exist
          setMappings([
            { trade: "Foundation / Concrete", cost_code: "01-100", category: "subcontractors" },
            { trade: "Framing", cost_code: "01-200", category: "labor" },
            { trade: "Plumbing", cost_code: "02-100", category: "subcontractors" },
            { trade: "Electrical", cost_code: "02-200", category: "subcontractors" },
            { trade: "HVAC", cost_code: "02-300", category: "subcontractors" },
            { trade: "Drywall", cost_code: "03-100", category: "materials" },
            { trade: "Paint", cost_code: "03-200", category: "materials" },
          ]);
        }
      } catch (error) {
        console.error("Failed to load mappings", error);
        // Use defaults on error
        setMappings([
          { trade: "Foundation / Concrete", cost_code: "01-100", category: "subcontractors" },
          { trade: "Framing", cost_code: "01-200", category: "labor" },
          { trade: "Plumbing", cost_code: "02-100", category: "subcontractors" },
          { trade: "Electrical", cost_code: "02-200", category: "subcontractors" },
          { trade: "HVAC", cost_code: "02-300", category: "subcontractors" },
          { trade: "Drywall", cost_code: "03-100", category: "materials" },
          { trade: "Paint", cost_code: "03-200", category: "materials" },
        ]);
      } finally {
        setLoading(false);
      }
    };
    if (isAdmin) {
      fetchMappings();
    } else {
      setLoading(false);
    }
  }, [isAdmin]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save each mapping
      for (const mapping of mappings) {
        await API.post("/trade-phase-mappings", {
          trade_phase: mapping.trade,
          cost_code: mapping.cost_code,
          budget_category: mapping.category
        });
      }
      toast.success("Mappings saved");
    } catch (error) {
      toast.error("Failed to save mappings");
    } finally {
      setSaving(false);
    }
  };

  const handleAddTrade = () => {
    setMappings([...mappings, { trade: "New Trade", cost_code: "", category: "subcontractors" }]);
  };

  const handleRemoveTrade = (index) => {
    setMappings(mappings.filter((_, i) => i !== index));
  };

  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <Lock className="h-12 w-12 mx-auto text-slate-300 mb-4" />
        <p className="text-slate-400">Admin access required</p>
      </div>
    );
  }

  if (loading) {
    return <div className="text-center py-8 text-slate-400">Loading mappings...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-chivo font-bold text-white">Trade / Phase Mapping</h2>
        <p className="text-slate-400 text-sm mt-1">Map user-facing trades to internal cost codes</p>
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="pt-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-300">Trade / Phase</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-300">Cost Code</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-300">Budget Category</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {mappings.map((mapping, index) => (
                  <tr key={index} className="border-b last:border-0">
                    <td className="py-3 px-4 text-sm text-white">{mapping.trade}</td>
                    <td className="py-3 px-4">
                      <Input
                        value={mapping.cost_code}
                        className="h-8 w-24"
                        onChange={(e) => {
                          const updated = [...mappings];
                          updated[index].cost_code = e.target.value;
                          setMappings(updated);
                        }}
                      />
                    </td>
                    <td className="py-3 px-4">
                      <Select
                        value={mapping.category}
                        onValueChange={(v) => {
                          const updated = [...mappings];
                          updated[index].category = v;
                          setMappings(updated);
                        }}
                      >
                        <SelectTrigger className="h-8 w-36">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="labor">Labor</SelectItem>
                          <SelectItem value="materials">Materials</SelectItem>
                          <SelectItem value="subcontractors">Subcontractors</SelectItem>
                          <SelectItem value="equipment">Equipment</SelectItem>
                          <SelectItem value="permits_fees">Permits & Fees</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-3 px-4">
                      <Button variant="ghost" size="sm" onClick={() => handleRemoveTrade(index)}>
                        <Trash2 size={14} className="text-slate-400" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" onClick={handleAddTrade} className="border-slate-700 text-slate-300 hover:bg-slate-800">
          <Plus size={16} className="mr-2" />
          Add Trade
        </Button>
        <Button onClick={handleSave} disabled={saving} className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">
          <Save size={16} className="mr-2" />
          {saving ? "Saving..." : "Save Mappings"}
        </Button>
      </div>
    </div>
  );
};

const ChangeOrderRules = () => {
  const [form, setForm] = useState({
    require_co_over_budget: true,
    auto_link_milestones: true,
    approval_routing: "pm_office_client",
  });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-chivo font-bold text-white">Change Order Rules</h2>
        <p className="text-slate-400 text-sm mt-1">Configure change order workflows</p>
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="pt-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-white">Require CO When Over Budget</p>
              <p className="text-sm text-slate-400">Automatically create CO when costs exceed budget</p>
            </div>
            <Switch
              checked={form.require_co_over_budget}
              onCheckedChange={(checked) => setForm({ ...form, require_co_over_budget: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-white">Auto-Link to SOW Milestones</p>
              <p className="text-sm text-slate-400">Associate change orders with affected milestones</p>
            </div>
            <Switch
              checked={form.auto_link_milestones}
              onCheckedChange={(checked) => setForm({ ...form, auto_link_milestones: checked })}
            />
          </div>

          <div>
            <label className="text-sm font-medium text-slate-300 mb-2 block">Approval Routing</label>
            <Select value={form.approval_routing} onValueChange={(v) => setForm({ ...form, approval_routing: v })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pm_office_client">PM → Office → Client</SelectItem>
                <SelectItem value="pm_client">PM → Client</SelectItem>
                <SelectItem value="office_client">Office → Client</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button>
          <Save size={16} className="mr-2" />
          Save Changes
        </Button>
      </div>
    </div>
  );
};

// ================== FINANCE & PAYMENTS SECTION ==================

const PaymentsSetup = () => {
  const [connected, setConnected] = useState(true);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-chivo font-bold text-white">Payments Setup</h2>
        <p className="text-slate-400 text-sm mt-1">Configure payment processing</p>
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between p-4 bg-slate-900 rounded-lg border border-slate-800">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-purple-100 flex items-center justify-center">
                <CreditCard className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="font-medium text-white">Stripe</p>
                <p className="text-sm text-slate-400">
                  {connected ? "Connected · Test mode" : "Not connected"}
                </p>
              </div>
            </div>
            {connected ? (
              <Badge className="bg-emerald-500/20 text-emerald-400">Connected</Badge>
            ) : (
              <Button className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">Connect Stripe</Button>
            )}
          </div>

          {connected && (
            <div className="mt-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-white">Transaction Fee Visibility</p>
                  <p className="text-sm text-slate-400">Show processing fees to clients</p>
                </div>
                <Switch />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-white">Deposit Timing</p>
                  <p className="text-sm text-slate-400">When funds are deposited</p>
                </div>
                <Badge className="bg-slate-700 text-slate-300">2 business days</Badge>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const ClientPaymentsSettings = () => {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-chivo font-bold text-white">Client Payments</h2>
        <p className="text-slate-400 text-sm mt-1">View and manage client payments</p>
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <DollarSign className="h-10 w-10 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-400">No payments yet</p>
            <p className="text-sm text-slate-400 mt-1">Client payments will appear here</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const Deposits = () => {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-chivo font-bold text-white">Deposits</h2>
        <p className="text-slate-400 text-sm mt-1">View deposit history and payouts</p>
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <RefreshCw className="h-10 w-10 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-400">No deposits yet</p>
            <p className="text-sm text-slate-400 mt-1">Payouts will appear here once payments are received</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const AccountingSync = () => {
  const [enabled, setEnabled] = useState(false);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-chivo font-bold text-white">Accounting Sync</h2>
        <p className="text-slate-400 text-sm mt-1">Connect to your accounting software</p>
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="pt-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-white">Enable Accounting Sync</p>
              <p className="text-sm text-slate-400">Sync data with your accounting software</p>
            </div>
            <Switch checked={enabled} onCheckedChange={setEnabled} />
          </div>

          {enabled && (
            <>
              <div>
                <label className="text-sm font-medium text-slate-300 mb-2 block">Accounting Provider</label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { id: "quickbooks", label: "QuickBooks", icon: "📊" },
                    { id: "xero", label: "Xero", icon: "📈" },
                  ].map((provider) => (
                    <button
                      key={provider.id}
                      className="p-4 rounded-lg border-2 border-slate-700 hover:border-slate-300 text-center"
                    >
                      <span className="text-2xl">{provider.icon}</span>
                      <p className="font-medium text-white mt-1">{provider.label}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-300 mb-2 block">Sync Direction</label>
                <Select defaultValue="two_way">
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="push">Push (to accounting)</SelectItem>
                    <SelectItem value="pull">Pull (from accounting)</SelectItem>
                    <SelectItem value="two_way">Two-way sync</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ================== SYSTEM & LEGAL SECTION ==================

const TermsConditions = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [newTemplate, setNewTemplate] = useState({ name: "", content: "", template_type: "company" });

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await API.get("/settings/terms-templates");
        setTemplates(response.data);
      } catch (error) {
        console.error("Failed to load templates", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTemplates();
  }, []);

  const handleAddTemplate = async () => {
    try {
      const response = await API.post("/settings/terms-templates", newTemplate);
      setTemplates([...templates, response.data]);
      setAddDialogOpen(false);
      setNewTemplate({ name: "", content: "", template_type: "company" });
      toast.success("Template created");
    } catch (error) {
      toast.error("Failed to create template");
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      await API.delete(`/settings/terms-templates/${templateId}`);
      setTemplates(templates.filter(t => t.template_id !== templateId));
      toast.success("Template deleted");
    } catch (error) {
      toast.error("Failed to delete template");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-chivo font-bold text-white">Terms & Conditions</h2>
          <p className="text-slate-400 text-sm mt-1">Manage your contract templates</p>
        </div>
        <Button onClick={() => setAddDialogOpen(true)} className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">
          <Plus size={16} className="mr-2" />
          New Template
        </Button>
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="pt-6">
          {loading ? (
            <div className="text-center py-8 text-slate-400">Loading templates...</div>
          ) : templates.length === 0 ? (
            <div className="text-center py-8">
              <FileCheck className="h-10 w-10 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-400">No terms templates yet</p>
              <Button className="mt-3 bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold" onClick={() => setAddDialogOpen(true)}>
                Add First Template
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {templates.map((template) => (
                <div key={template.template_id} className="flex items-center justify-between p-4 bg-slate-900 rounded-lg border border-slate-800">
                  <div className="flex items-center gap-3">
                    <FileCheck className="h-5 w-5 text-slate-400" />
                    <div>
                      <p className="font-medium text-white">{template.name}</p>
                      <p className="text-sm text-slate-400">
                        {template.template_type === "company" ? "Company-wide" : "Project-specific"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => { setSelectedTemplate(template); setEditDialogOpen(true); }}>
                      <Eye size={14} />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteTemplate(template.template_id)}>
                      <Trash2 size={14} className="text-slate-400" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Template Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">Add Terms Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-300 mb-1 block">Template Name</label>
              <Input
                value={newTemplate.name}
                onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                placeholder="e.g., Standard Remodel Terms"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300 mb-1 block">Template Type</label>
              <Select value={newTemplate.template_type} onValueChange={(v) => setNewTemplate({ ...newTemplate, template_type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="company">Company-wide</SelectItem>
                  <SelectItem value="project">Project-specific</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300 mb-1 block">Content</label>
              <Textarea
                value={newTemplate.content}
                onChange={(e) => setNewTemplate({ ...newTemplate, content: e.target.value })}
                placeholder="Enter your terms and conditions text..."
                rows={6}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAddDialogOpen(false)} className="border-slate-700 text-slate-300 hover:bg-slate-800">Cancel</Button>
              <Button onClick={handleAddTemplate} disabled={!newTemplate.name} className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">Add Template</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Template Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">{selectedTemplate?.name}</DialogTitle>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto">
            <p className="text-sm text-slate-400 whitespace-pre-wrap">
              {selectedTemplate?.content || "No content"}
            </p>
          </div>
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="border-slate-700 text-slate-300 hover:bg-slate-800">Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const LegalPrivacy = () => {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-chivo font-bold text-white">Legal & Privacy</h2>
        <p className="text-slate-400 text-sm mt-1">Legal documents and policies</p>
      </div>

      <div className="space-y-4">
        {[
          { title: "Terms of Service", description: "Platform usage terms" },
          { title: "Privacy Policy", description: "How we handle your data" },
          { title: "Data Retention", description: "Coming soon", disabled: true },
        ].map((item) => (
          <Card key={item.title} className={item.disabled ? "opacity-50" : ""}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-white">{item.title}</p>
                  <p className="text-sm text-slate-400">{item.description}</p>
                </div>
                <Button variant="outline" disabled={item.disabled} className="border-slate-700 text-slate-300 hover:bg-slate-800">
                  View
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

const SubscriptionBilling = () => {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-chivo font-bold text-white">Subscription & Billing</h2>
        <p className="text-slate-400 text-sm mt-1">Manage your plan and billing</p>
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between p-4 bg-gradient-to-r from-emerald-500/10 to-emerald-500/5 rounded-lg border border-emerald-500/20">
            <div>
              <Badge className="bg-emerald-500 text-slate-900 mb-2">Pro Plan</Badge>
              <p className="font-medium text-white">$99/month</p>
              <p className="text-sm text-slate-400">Next billing: February 1, 2025</p>
            </div>
            <Button variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800">Change Plan</Button>
          </div>

          <div className="mt-6">
            <h3 className="font-medium text-white mb-3">Usage This Month</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-400">Projects</span>
                <span className="text-sm font-medium">2 / Unlimited</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-400">Team Members</span>
                <span className="text-sm font-medium">1 / 10</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-400">AI Estimates</span>
                <span className="text-sm font-medium">12 / 100</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const DangerZone = () => {
  const { isAdmin } = useSettings();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-chivo font-bold text-white">Account</h2>
        <p className="text-slate-400 text-sm mt-1">Manage your account settings</p>
      </div>

      {/* Sign Out Section - Available to all users */}
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-white">Sign Out</p>
              <p className="text-sm text-slate-400">Log out of your account on this device</p>
            </div>
            <Button variant="outline" onClick={handleLogout} className="border-slate-700 text-slate-300 hover:bg-slate-800">
              <LogOut size={16} className="mr-2" />
              Sign Out
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Admin-only danger zone */}
      {isAdmin ? (
        <>
          <div className="mt-8">
            <h2 className="text-xl font-chivo font-bold text-red-400">Danger Zone</h2>
            <p className="text-slate-400 text-sm mt-1">Irreversible actions - proceed with caution</p>
          </div>

          <Card className="border-red-500/30 bg-slate-900">
            <CardContent className="pt-6 space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-white">Export All Data</p>
                  <p className="text-sm text-slate-400">Download all your company data</p>
                </div>
                <Button variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800">
                  <Download size={16} className="mr-2" />
                  Export
                </Button>
              </div>

              <div className="border-t border-slate-800 pt-6 flex items-center justify-between">
                <div>
                  <p className="font-medium text-white">Transfer Ownership</p>
                  <p className="text-sm text-slate-400">Transfer account to another admin</p>
                </div>
                <Button variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800">Transfer</Button>
              </div>

              <div className="border-t border-slate-800 pt-6 flex items-center justify-between">
                <div>
                  <p className="font-medium text-red-400">Delete Account</p>
                  <p className="text-sm text-slate-400">Permanently delete your account and all data</p>
                </div>
                <Button className="bg-red-500 hover:bg-red-400 text-white font-semibold">
                  <Trash2 size={16} className="mr-2" />
                  Delete Account
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <div className="text-center py-8 text-slate-400">
          <Lock className="h-8 w-8 mx-auto text-slate-300 mb-2" />
          <p className="text-sm">Admin actions require elevated permissions</p>
        </div>
      )}
    </div>
  );
};

// ================== DATA IMPORT PAGE ==================
const DataImportPage = () => {
  const [activeTab, setActiveTab] = useState('guide');
  const [importType, setImportType] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = React.useRef(null);

  const importTypes = [
    { 
      id: 'clients', 
      label: 'Clients & Contacts', 
      icon: Users,
      description: 'Import client information, contact details, and addresses',
      fields: ['name*', 'email', 'phone', 'address', 'city', 'state', 'zip', 'notes', 'source', 'status']
    },
    { 
      id: 'leads', 
      label: 'Leads', 
      icon: Users,
      description: 'Import sales leads and prospects',
      fields: ['name*', 'email', 'phone', 'address', 'source', 'status', 'notes', 'estimated_value', 'created_date']
    },
    { 
      id: 'estimates', 
      label: 'Estimates', 
      icon: FileText,
      description: 'Import existing estimates - auto-links to leads/clients by email or name',
      fields: ['project_name*', 'amount', 'status', 'date', 'notes', 'lead_id', 'client_id', 'lead_email', 'client_email', 'lead_name', 'client_name', 'line_items'],
      linkingNote: 'Use lead_email/client_email OR lead_name/client_name to auto-link to existing records'
    },
    { 
      id: 'projects', 
      label: 'Projects', 
      icon: FolderCog,
      description: 'Import projects - auto-links to clients and estimates',
      fields: ['job_name*', 'client_name', 'client_email', 'client_id', 'estimate_id', 'address', 'contract_value', 'start_date', 'end_date', 'status'],
      linkingNote: 'Use client_email to auto-link. Projects also link to estimates by matching job_name to project_name'
    },
    { 
      id: 'invoices', 
      label: 'Invoices', 
      icon: CreditCard,
      description: 'Import invoice history - auto-links to clients and projects',
      fields: ['invoice_number*', 'amount*', 'client_name', 'client_email', 'client_id', 'project_name', 'project_id', 'date', 'due_date', 'status', 'amount_paid'],
      linkingNote: 'Use client_email to auto-link to clients. Use project_name to auto-link to projects'
    },
    { 
      id: 'expenses', 
      label: 'Operating Expenses', 
      icon: DollarSign,
      description: 'Import expense records for P&L tracking',
      fields: ['date*', 'category*', 'amount*', 'description', 'vendor', 'payment_method', 'notes']
    },
    { 
      id: 'subcontractors', 
      label: 'Subcontractors', 
      icon: Users,
      description: 'Import subcontractor/vendor database',
      fields: ['company_name*', 'contact_name', 'email', 'phone', 'trade', 'license_number', 'insurance_expiry', 'rating']
    },
    { 
      id: 'team', 
      label: 'Team Members', 
      icon: Users,
      description: 'Import employee/team member records',
      fields: ['name*', 'email*', 'phone', 'role', 'hourly_rate', 'start_date']
    },
  ];

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setImportResults(null);
    }
  };

  const handleImport = async () => {
    if (!selectedFile || !importType) {
      toast.error("Please select a file and import type");
      return;
    }

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('type', importType);

      const response = await API.post('/import/data', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setImportResults(response.data);
      toast.success(`Successfully imported ${response.data.imported} records`);
    } catch (error) {
      console.error("Import error:", error);
      toast.error(error.response?.data?.detail || "Import failed");
      setImportResults({ error: error.response?.data?.detail || "Import failed" });
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = (typeId) => {
    const type = importTypes.find(t => t.id === typeId);
    if (!type) return;

    // Create CSV template with headers
    const headers = type.fields.join(',');
    const exampleRow = type.fields.map(f => `example_${f}`).join(',');
    const csvContent = `${headers}\n${exampleRow}`;
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${typeId}_import_template.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    toast.success(`Downloaded ${type.label} template`);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-chivo font-bold text-white">Import Data</h2>
        <p className="text-slate-400 mt-1">Migrate your data from another system to ConstructPro</p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-slate-800 pb-2">
        <Button
          variant={activeTab === 'guide' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('guide')}
          className={activeTab === 'guide' ? 'bg-emerald-600' : ''}
        >
          <BookOpen size={16} className="mr-2" />
          User Guide
        </Button>
        <Button
          variant={activeTab === 'import' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('import')}
          className={activeTab === 'import' ? 'bg-emerald-600' : ''}
        >
          <Upload size={16} className="mr-2" />
          Import Data
        </Button>
        <Button
          variant={activeTab === 'templates' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('templates')}
          className={activeTab === 'templates' ? 'bg-emerald-600' : ''}
        >
          <Download size={16} className="mr-2" />
          Templates
        </Button>
      </div>

      {/* User Guide Tab */}
      {activeTab === 'guide' && (
        <div className="space-y-6">
          <Card className="bg-gradient-to-br from-emerald-900/30 to-slate-900 border-emerald-700/30">
            <CardHeader>
              <CardTitle className="text-emerald-400 flex items-center gap-2">
                <BookOpen size={20} />
                Data Migration User Guide
              </CardTitle>
              <CardDescription>Complete instructions for importing your data into ConstructPro</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 text-slate-300">
              {/* Overview */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">Overview</h3>
                <p className="mb-4">
                  ConstructPro makes it easy to migrate your existing data from spreadsheets or other construction management software. 
                  This guide will walk you through the process step by step.
                </p>
                <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700">
                  <p className="font-semibold text-white mb-2">Supported Data Types:</p>
                  <ul className="grid grid-cols-2 gap-2 text-sm">
                    <li className="flex items-center gap-2"><Check size={14} className="text-emerald-400" /> Clients & Contacts</li>
                    <li className="flex items-center gap-2"><Check size={14} className="text-emerald-400" /> Leads</li>
                    <li className="flex items-center gap-2"><Check size={14} className="text-emerald-400" /> Estimates</li>
                    <li className="flex items-center gap-2"><Check size={14} className="text-emerald-400" /> Projects</li>
                    <li className="flex items-center gap-2"><Check size={14} className="text-emerald-400" /> Invoices</li>
                    <li className="flex items-center gap-2"><Check size={14} className="text-emerald-400" /> Expenses</li>
                    <li className="flex items-center gap-2"><Check size={14} className="text-emerald-400" /> Subcontractors</li>
                    <li className="flex items-center gap-2"><Check size={14} className="text-emerald-400" /> Team Members</li>
                  </ul>
                </div>
              </div>

              {/* File Format Requirements */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">File Format Requirements</h3>
                <div className="space-y-3">
                  <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700">
                    <p className="font-semibold text-emerald-400 mb-2">Accepted Formats:</p>
                    <ul className="space-y-1 text-sm">
                      <li><Badge className="bg-emerald-500/20 text-emerald-400 mr-2">.CSV</Badge> Comma-Separated Values (Recommended)</li>
                      <li><Badge className="bg-blue-500/20 text-blue-400 mr-2">.XLSX</Badge> Microsoft Excel</li>
                      <li><Badge className="bg-purple-500/20 text-purple-400 mr-2">.XLS</Badge> Legacy Excel Format</li>
                    </ul>
                  </div>
                  <div className="bg-amber-900/20 p-4 rounded-lg border border-amber-700/30">
                    <p className="font-semibold text-amber-400 mb-2 flex items-center gap-2">
                      <AlertTriangle size={16} />
                      Important Requirements:
                    </p>
                    <ul className="space-y-1 text-sm text-amber-200">
                      <li>• First row MUST contain column headers</li>
                      <li>• Column headers must match the template exactly</li>
                      <li>• Date format: YYYY-MM-DD (e.g., 2026-01-15)</li>
                      <li>• Currency values: Numbers only, no $ symbols</li>
                      <li>• Maximum file size: 10MB</li>
                      <li>• Maximum rows per import: 5,000</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Step by Step Instructions */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">Step-by-Step Import Process</h3>
                <div className="space-y-4">
                  <div className="flex gap-4 items-start">
                    <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-slate-900 font-bold shrink-0">1</div>
                    <div>
                      <p className="font-semibold text-white">Download the Template</p>
                      <p className="text-sm text-slate-400">Go to the Templates tab and download the template for the data type you want to import. Each template includes the required column headers and an example row.</p>
                    </div>
                  </div>
                  <div className="flex gap-4 items-start">
                    <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-slate-900 font-bold shrink-0">2</div>
                    <div>
                      <p className="font-semibold text-white">Prepare Your Data</p>
                      <p className="text-sm text-slate-400">Copy your existing data into the template. Make sure column headers match exactly. Delete the example row before importing.</p>
                    </div>
                  </div>
                  <div className="flex gap-4 items-start">
                    <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-slate-900 font-bold shrink-0">3</div>
                    <div>
                      <p className="font-semibold text-white">Validate Your File</p>
                      <p className="text-sm text-slate-400">Check for empty required fields, proper date formats, and numeric values for amounts. Remove any special characters from phone numbers.</p>
                    </div>
                  </div>
                  <div className="flex gap-4 items-start">
                    <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-slate-900 font-bold shrink-0">4</div>
                    <div>
                      <p className="font-semibold text-white">Upload & Import</p>
                      <p className="text-sm text-slate-400">Go to the Import Data tab, select your data type, choose your file, and click Import. Review the results for any errors.</p>
                    </div>
                  </div>
                  <div className="flex gap-4 items-start">
                    <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-slate-900 font-bold shrink-0">5</div>
                    <div>
                      <p className="font-semibold text-white">Review & Verify</p>
                      <p className="text-sm text-slate-400">After import, navigate to the relevant section (Clients, Projects, etc.) to verify your data was imported correctly.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Auto-Linking Feature */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                  <Link2 size={20} className="text-blue-400" />
                  Smart Auto-Linking
                </h3>
                <div className="bg-blue-900/20 p-4 rounded-lg border border-blue-700/30 mb-4">
                  <p className="text-sm text-slate-300 mb-3">
                    ConstructPro automatically links your imported data to existing records. This means your estimates, projects, and invoices 
                    will be properly connected to leads and clients without manual work.
                  </p>
                </div>
                <div className="space-y-3">
                  <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700">
                    <p className="font-semibold text-emerald-400 mb-2">Estimates → Leads/Clients</p>
                    <p className="text-sm text-slate-400 mb-2">Include any of these columns to auto-link:</p>
                    <ul className="text-sm text-slate-400 space-y-1 ml-4">
                      <li>• <code className="bg-slate-700 px-1 rounded">lead_id</code> or <code className="bg-slate-700 px-1 rounded">client_id</code> - Direct ID if known</li>
                      <li>• <code className="bg-slate-700 px-1 rounded">lead_email</code> or <code className="bg-slate-700 px-1 rounded">client_email</code> - Matches by email address</li>
                      <li>• <code className="bg-slate-700 px-1 rounded">lead_name</code> or <code className="bg-slate-700 px-1 rounded">client_name</code> - Matches by name (case insensitive)</li>
                    </ul>
                  </div>
                  <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700">
                    <p className="font-semibold text-emerald-400 mb-2">Projects → Clients & Estimates</p>
                    <p className="text-sm text-slate-400 mb-2">Include any of these columns to auto-link:</p>
                    <ul className="text-sm text-slate-400 space-y-1 ml-4">
                      <li>• <code className="bg-slate-700 px-1 rounded">client_email</code> - Matches to existing client</li>
                      <li>• <code className="bg-slate-700 px-1 rounded">client_name</code> - Matches client by name</li>
                      <li>• <code className="bg-slate-700 px-1 rounded">estimate_id</code> - Links to specific estimate</li>
                      <li>• Projects also auto-link to estimates when <code className="bg-slate-700 px-1 rounded">job_name</code> matches an estimate&apos;s <code className="bg-slate-700 px-1 rounded">project_name</code></li>
                    </ul>
                  </div>
                  <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700">
                    <p className="font-semibold text-emerald-400 mb-2">Invoices → Clients & Projects</p>
                    <p className="text-sm text-slate-400 mb-2">Include any of these columns to auto-link:</p>
                    <ul className="text-sm text-slate-400 space-y-1 ml-4">
                      <li>• <code className="bg-slate-700 px-1 rounded">client_email</code> or <code className="bg-slate-700 px-1 rounded">client_name</code> - Links to client</li>
                      <li>• <code className="bg-slate-700 px-1 rounded">project_name</code> - Links to project by job name</li>
                    </ul>
                  </div>
                </div>
                <div className="bg-amber-900/20 p-4 rounded-lg border border-amber-700/30 mt-4">
                  <p className="font-semibold text-amber-400 mb-2 flex items-center gap-2">
                    <AlertTriangle size={16} />
                    Recommended Import Order
                  </p>
                  <p className="text-sm text-amber-200">
                    For best auto-linking results, import in this order: <strong>1. Clients</strong> → <strong>2. Leads</strong> → <strong>3. Estimates</strong> → <strong>4. Projects</strong> → <strong>5. Invoices</strong>
                  </p>
                </div>
              </div>

              {/* Data Type Specific Instructions */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">Data Type Specific Instructions</h3>
                <div className="space-y-3">
                  {importTypes.map((type) => {
                    const Icon = type.icon;
                    return (
                      <details key={type.id} className="bg-slate-800/50 rounded-lg border border-slate-700">
                        <summary className="p-4 cursor-pointer flex items-center gap-3 hover:bg-slate-800">
                          <Icon size={18} className="text-emerald-400" />
                          <span className="font-medium text-white">{type.label}</span>
                        </summary>
                        <div className="px-4 pb-4 pt-2 border-t border-slate-700 mt-2">
                          <p className="text-sm text-slate-400 mb-3">{type.description}</p>
                          {type.linkingNote && (
                            <div className="bg-blue-900/20 p-3 rounded-lg border border-blue-700/30 mb-3">
                              <p className="text-xs text-blue-300 flex items-start gap-2">
                                <Link2 size={14} className="mt-0.5 flex-shrink-0" />
                                <span>{type.linkingNote}</span>
                              </p>
                            </div>
                          )}
                          <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Fields (* = Required):</p>
                          <div className="flex flex-wrap gap-1">
                            {type.fields.map((field) => {
                              const isRequired = field.endsWith('*');
                              const fieldName = isRequired ? field.slice(0, -1) : field;
                              return (
                                <Badge 
                                  key={field} 
                                  className={isRequired ? "bg-emerald-500/20 text-emerald-400" : "bg-slate-700 text-slate-400"}
                                >
                                  {fieldName}
                                  {isRequired && <span className="ml-1 text-emerald-600">*</span>}
                                </Badge>
                              );
                            })}
                          </div>
                        </div>
                      </details>
                    );
                  })}
                </div>
              </div>

              {/* Troubleshooting */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">Troubleshooting Common Issues</h3>
                <div className="space-y-3">
                  <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700">
                    <p className="font-semibold text-red-400 mb-2">Error: Invalid date format</p>
                    <p className="text-sm text-slate-400">Ensure all dates are in YYYY-MM-DD format (e.g., 2026-01-15). Remove any text like Jan or January.</p>
                  </div>
                  <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700">
                    <p className="font-semibold text-red-400 mb-2">Error: Missing required field</p>
                    <p className="text-sm text-slate-400">Check that all required fields have values. Empty cells in required columns will cause errors.</p>
                  </div>
                  <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700">
                    <p className="font-semibold text-red-400 mb-2">Error: Invalid amount format</p>
                    <p className="text-sm text-slate-400">Remove currency symbols ($), commas, and text from amount fields. Use numbers only (e.g., 15000.00).</p>
                  </div>
                  <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700">
                    <p className="font-semibold text-red-400 mb-2">Error: File too large</p>
                    <p className="text-sm text-slate-400">Split your data into multiple files with fewer than 5,000 rows each, then import separately.</p>
                  </div>
                </div>
              </div>

              {/* Contact Support */}
              <div className="bg-blue-900/20 p-4 rounded-lg border border-blue-700/30">
                <p className="font-semibold text-blue-400 mb-2">Need Help?</p>
                <p className="text-sm text-slate-400">
                  If you encounter issues during import or need assistance migrating complex data, contact our support team. 
                  We offer complimentary data migration assistance for customers switching from other platforms.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Import Data Tab */}
      {activeTab === 'import' && (
        <div className="space-y-6">
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="text-white">Select Data Type</CardTitle>
              <CardDescription>Choose what type of data you want to import</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {importTypes.map((type) => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.id}
                      onClick={() => setImportType(type.id)}
                      className={`p-4 rounded-lg border transition-all text-left ${
                        importType === type.id 
                          ? 'border-emerald-500 bg-emerald-500/10' 
                          : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                      }`}
                    >
                      <Icon size={24} className={importType === type.id ? 'text-emerald-400' : 'text-slate-400'} />
                      <p className="font-medium text-white mt-2">{type.label}</p>
                      <p className="text-xs text-slate-500 mt-1">{type.description}</p>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {importType && (
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white">Upload File</CardTitle>
                <CardDescription>
                  Upload your {importTypes.find(t => t.id === importType)?.label} data file (.csv)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Show linking note if available */}
                {importTypes.find(t => t.id === importType)?.linkingNote && (
                  <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-3">
                    <p className="text-sm text-blue-300 flex items-start gap-2">
                      <Link2 size={16} className="mt-0.5 flex-shrink-0" />
                      <span><strong>Auto-Linking:</strong> {importTypes.find(t => t.id === importType)?.linkingNote}</span>
                    </p>
                  </div>
                )}
                
                {/* Show required fields */}
                <div className="text-xs text-slate-500">
                  <span className="text-amber-400">*</span> = Required field. Fields: {importTypes.find(t => t.id === importType)?.fields?.join(', ')}
                </div>

                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept=".csv"
                  className="hidden"
                />
                
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-slate-700 rounded-lg p-8 text-center cursor-pointer hover:border-emerald-500/50 transition-colors"
                >
                  <Upload size={40} className="mx-auto text-slate-500 mb-3" />
                  {selectedFile ? (
                    <>
                      <p className="text-emerald-400 font-medium">{selectedFile.name}</p>
                      <p className="text-sm text-slate-500 mt-1">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                    </>
                  ) : (
                    <>
                      <p className="text-slate-400">Click to select a file or drag and drop</p>
                      <p className="text-sm text-slate-500 mt-1">CSV format (max 5000 rows)</p>
                    </>
                  )}
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={() => downloadTemplate(importType)}
                    variant="outline"
                    className="border-slate-700"
                  >
                    <Download size={16} className="mr-2" />
                    Download Template
                  </Button>
                  <Button
                    onClick={handleImport}
                    disabled={!selectedFile || importing}
                    className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 flex-1"
                  >
                    {importing ? (
                      <>
                        <Loader2 size={16} className="mr-2 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Upload size={16} className="mr-2" />
                        Start Import
                      </>
                    )}
                  </Button>
                </div>

                {importResults && (
                  <div className={`p-4 rounded-lg border ${importResults.error ? 'bg-red-900/20 border-red-700/30' : 'bg-emerald-900/20 border-emerald-700/30'}`}>
                    {importResults.error ? (
                      <>
                        <p className="font-semibold text-red-400 mb-2 flex items-center gap-2">
                          <X size={16} />
                          Import Failed
                        </p>
                        <p className="text-sm text-red-300">{importResults.error}</p>
                      </>
                    ) : (
                      <>
                        <p className="font-semibold text-emerald-400 mb-2 flex items-center gap-2">
                          <Check size={16} />
                          Import Successful
                        </p>
                        <div className="grid grid-cols-3 gap-4 mt-3">
                          <div className="text-center">
                            <p className="text-2xl font-bold text-white">{importResults.imported || 0}</p>
                            <p className="text-xs text-slate-400">Imported</p>
                          </div>
                          <div className="text-center">
                            <p className="text-2xl font-bold text-amber-400">{importResults.skipped || 0}</p>
                            <p className="text-xs text-slate-400">Skipped</p>
                          </div>
                          <div className="text-center">
                            <p className="text-2xl font-bold text-red-400">{importResults.errors || 0}</p>
                            <p className="text-xs text-slate-400">Errors</p>
                          </div>
                        </div>
                        {importResults.errorDetails && importResults.errorDetails.length > 0 && (
                          <div className="mt-3 text-sm text-amber-300">
                            <p className="font-medium">Error details:</p>
                            <ul className="list-disc list-inside">
                              {importResults.errorDetails.slice(0, 5).map((err, i) => (
                                <li key={i}>{err}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="space-y-4">
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="text-white">Download Import Templates</CardTitle>
              <CardDescription>Use these templates to prepare your data for import</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3">
                {importTypes.map((type) => {
                  const Icon = type.icon;
                  return (
                    <div 
                      key={type.id}
                      className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700"
                    >
                      <div className="flex items-center gap-3">
                        <Icon size={20} className="text-emerald-400" />
                        <div>
                          <p className="font-medium text-white">{type.label}</p>
                          <p className="text-sm text-slate-500">{type.fields.length} columns</p>
                        </div>
                      </div>
                      <Button
                        onClick={() => downloadTemplate(type.id)}
                        variant="outline"
                        size="sm"
                        className="border-slate-700"
                      >
                        <Download size={14} className="mr-2" />
                        Download CSV
                      </Button>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// ================== DATA EXPORT PAGE ==================
const DataExportPage = () => {
  const [exporting, setExporting] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState([]);

  const exportTypes = [
    { id: 'clients', label: 'Clients & Contacts', count: 0 },
    { id: 'leads', label: 'Leads', count: 0 },
    { id: 'estimates', label: 'Estimates', count: 0 },
    { id: 'projects', label: 'Projects', count: 0 },
    { id: 'invoices', label: 'Invoices', count: 0 },
    { id: 'expenses', label: 'Expenses', count: 0 },
    { id: 'subcontractors', label: 'Subcontractors', count: 0 },
    { id: 'team', label: 'Team Members', count: 0 },
    { id: 'time_entries', label: 'Time Entries', count: 0 },
    { id: 'purchase_orders', label: 'Purchase Orders', count: 0 },
  ];

  const toggleType = (typeId) => {
    setSelectedTypes(prev => 
      prev.includes(typeId) 
        ? prev.filter(t => t !== typeId)
        : [...prev, typeId]
    );
  };

  const handleExport = async () => {
    if (selectedTypes.length === 0) {
      toast.error("Please select at least one data type to export");
      return;
    }

    setExporting(true);
    try {
      const response = await API.post('/export/data', { types: selectedTypes }, { responseType: 'blob' });
      
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `constructpro_export_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Data exported successfully");
    } catch (error) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-chivo font-bold text-white">Export Data</h2>
        <p className="text-slate-400 mt-1">Download your data in CSV format for backup or migration</p>
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white">Select Data to Export</CardTitle>
          <CardDescription>Choose which data types you want to include in your export</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {exportTypes.map((type) => (
              <button
                key={type.id}
                onClick={() => toggleType(type.id)}
                className={`p-3 rounded-lg border transition-all text-left flex items-center gap-3 ${
                  selectedTypes.includes(type.id)
                    ? 'border-emerald-500 bg-emerald-500/10'
                    : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                }`}
              >
                <div className={`w-5 h-5 rounded border flex items-center justify-center ${
                  selectedTypes.includes(type.id) 
                    ? 'border-emerald-500 bg-emerald-500' 
                    : 'border-slate-600'
                }`}>
                  {selectedTypes.includes(type.id) && <Check size={14} className="text-slate-900" />}
                </div>
                <span className="text-white">{type.label}</span>
              </button>
            ))}
          </div>

          <div className="flex gap-3 pt-4 border-t border-slate-800">
            <Button
              onClick={() => setSelectedTypes(exportTypes.map(t => t.id))}
              variant="outline"
              className="border-slate-700"
            >
              Select All
            </Button>
            <Button
              onClick={() => setSelectedTypes([])}
              variant="outline"
              className="border-slate-700"
            >
              Clear Selection
            </Button>
            <Button
              onClick={handleExport}
              disabled={selectedTypes.length === 0 || exporting}
              className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 flex-1"
            >
              {exporting ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download size={16} className="mr-2" />
                  Export Selected ({selectedTypes.length})
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-blue-900/20 border-blue-700/30">
        <CardContent className="pt-6">
          <p className="text-blue-400 font-medium mb-2">About Data Export</p>
          <ul className="text-sm text-slate-400 space-y-1">
            <li>• Each data type is exported as a separate CSV file</li>
            <li>• All files are bundled into a single ZIP archive</li>
            <li>• Exports include all historical data in your account</li>
            <li>• Use exports for backup or migrating to another system</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsPage;
